import { useState, useEffect } from "react";

// ─── URL ROUTING ──────────────────────────────────────────────────────────────
function slugify(str) {
  return (str || "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Map page names → URL paths
var PAGE_PATHS = {
  "Home":      "/",
  "Solutions": "/solutions",
  "Pricing":   "/pricing",
  "About":     "/about",
  "FAQ":       "/faq",
  "Blog":      "/blog",
  "Privacy":   "/privacy",
  "Terms":     "/terms"
};

// Map URL paths → page names
var PATH_PAGES = {};
Object.keys(PAGE_PATHS).forEach(function(k) { PATH_PAGES[PAGE_PATHS[k]] = k; });

function getInitialRoute() {
  var path = window.location.pathname;
  // Blog post: /blog/<slug>
  if (path.indexOf("/blog/") === 0) {
    return { page: "Blog", blogSlug: path.replace("/blog/", "") };
  }
  var pg = PATH_PAGES[path] || PATH_PAGES[path.replace(/\/$/, "")] || "Home";
  return { page: pg, blogSlug: null };
}

// ─── SEO META MAP ─────────────────────────────────────────────────────────────
var SEO = {
  "Home": {
    title: "StrategicGreen.ai — Agentic AI for Carbon Accounting & GHG Reporting",
    description: "Automatically extract utility bill data, calculate GHG Scope 1, 2 & 3 emissions, and benchmark across your entire portfolio. No templates, no manual mapping.",
    keywords: "carbon accounting software, GHG reporting, utility bill extraction, Scope 1 2 3 emissions, ESG data management, building energy carbon reporting, automated utility data, sustainability AI"
  },
  "Solutions": {
    title: "Solutions — Utility AI & BIM Carbon Intelligence | StrategicGreen.ai",
    description: "Three AI solutions for the built environment: StrategicGreen.ai for full GHG accounting, StrategicGreen.ai LITE for instant bill extraction, and BIM AI for embodied carbon.",
    keywords: "utility bill carbon accounting, BIM embodied carbon, GHG scope reporting tool, utility data extraction AI, ESG software built environment"
  },
  "Pricing": {
    title: "Pricing — StrategicGreen.ai",
    description: "Flexible pricing for sustainability teams. Start free with StrategicGreen.ai LITE or unlock full GHG portfolio intelligence with StrategicGreen.ai.",
    keywords: "carbon accounting software pricing, GHG reporting tool cost, ESG software plans"
  },
  "About": {
    title: "About Us — StrategicGreen.ai",
    description: "Built by sustainability and AI experts with over a decade of experience in the built environment. Learn the story behind StrategicGreen.ai.",
    keywords: "StrategicGreen.ai about, sustainability AI company, built environment ESG team"
  },
  "FAQ": {
    title: "FAQ — StrategicGreen.ai",
    description: "Answers to common questions about StrategicGreen.ai — how it works, what data it supports, integrations, and security.",
    keywords: "StrategicGreen.ai FAQ, carbon accounting questions, utility bill AI help"
  },
  "Blog": {
    title: "Blog — StrategicGreen.ai",
    description: "Insights on GHG reporting, utility data management, ESG compliance, and AI for the built environment.",
    keywords: "GHG reporting blog, carbon accounting insights, ESG built environment, utility data AI articles"
  },
  "Privacy": {
    title: "Privacy Policy — StrategicGreen.ai",
    description: "How StrategicGreen.ai collects, uses, and protects your personal data.",
    keywords: ""
  },
  "Terms": {
    title: "Terms of Service — StrategicGreen.ai",
    description: "Terms and conditions for using StrategicGreen.ai products and services.",
    keywords: ""
  }
};

function applyPageSEO(page, blogPost) {
  var BASE = "https://strategicgreenai.com";
  var title, description, canonical, image;

  if (blogPost) {
    title = blogPost.title + " | StrategicGreen.ai Blog";
    description = blogPost.excerpt || "Read this article on the StrategicGreen.ai blog.";
    canonical = BASE + "/blog/" + slugify(blogPost.title);
    image = (blogPost.coverImage && blogPost.coverImage.asset && blogPost.coverImage.asset.url)
      ? blogPost.coverImage.asset.url
      : BASE + "/og-image.png";
  } else {
    var meta = SEO[page] || SEO["Home"];
    title = meta.title;
    description = meta.description;
    canonical = BASE + (PAGE_PATHS[page] || "/");
    image = BASE + "/og-image.png";
  }

  document.title = title;

  function setMeta(selector, attr, value) {
    var el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      var parts = selector.replace("[","").replace("]","").split("=");
      el.setAttribute(parts[0].replace("meta","").trim(), parts[1].replace(/"/g,""));
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  }

  setMeta('meta[name="description"]',        "content", description);
  setMeta('meta[name="keywords"]',            "content", blogPost ? "sustainability blog, carbon accounting, GHG reporting, ESG" : (SEO[page] || SEO["Home"]).keywords);
  setMeta('meta[name="robots"]',              "content", "index, follow");
  setMeta('meta[property="og:title"]',        "content", title);
  setMeta('meta[property="og:description"]',  "content", description);
  setMeta('meta[property="og:url"]',          "content", canonical);
  setMeta('meta[property="og:type"]',         "content", blogPost ? "article" : "website");
  setMeta('meta[property="og:image"]',        "content", image);
  setMeta('meta[property="og:image:width"]',  "content", "1200");
  setMeta('meta[property="og:image:height"]', "content", "630");
  setMeta('meta[name="twitter:card"]',        "content", "summary_large_image");
  setMeta('meta[name="twitter:title"]',       "content", title);
  setMeta('meta[name="twitter:description"]', "content", description);
  setMeta('meta[name="twitter:image"]',       "content", image);

  var canon = document.querySelector('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
  }
  canon.setAttribute("href", canonical);
}

function navigate(page, blogPost) {
  var path;
  if (blogPost) {
    path = "/blog/" + slugify(blogPost.title);
  } else {
    path = PAGE_PATHS[page] || "/";
  }
  window.history.pushState({ page: page, blogSlug: blogPost ? slugify(blogPost.title) : null }, "", path);
}

// ─── SANITY CLIENT ────────────────────────────────────────────────────────────
var SANITY_PROJECT_ID = process.env.REACT_APP_SANITY_PROJECT_ID || "8qfgmidk";
var SANITY_DATASET    = process.env.REACT_APP_SANITY_DATASET    || "production";
var SANITY_API_VER    = "2024-01-01";
var SANITY_BASE       = "https://" + SANITY_PROJECT_ID + ".api.sanity.io/v" + SANITY_API_VER + "/data/query/" + SANITY_DATASET;

// Build a Sanity CDN image URL from an asset reference
function sanityImg(image, width) {
  if (!image) return null;

  // Case 1: asset was dereferenced (asset->) — has a direct url field
  if (image.asset && image.asset.url) {
    var base1 = image.asset.url;
    return width ? base1 + "?w=" + width + "&auto=format&fit=crop" : base1 + "?auto=format";
  }

  // Case 2: asset has _ref — build URL from ref string
  // ref format: image-<id>-<WxH>-<ext>
  var ref = null;
  if (image.asset && image.asset._ref) ref = image.asset._ref;
  else if (image._ref) ref = image._ref;
  if (!ref) return null;

  var withoutPrefix = ref.replace(/^image-/, "");
  var parts = withoutPrefix.split("-");
  if (parts.length < 3) return null;
  var ext = parts[parts.length - 1];
  var id = parts.slice(0, parts.length - 2).join("-");
  var base2 = "https://cdn.sanity.io/images/" + SANITY_PROJECT_ID + "/" + SANITY_DATASET + "/" + id + "." + ext;
  return width ? base2 + "?w=" + width + "&auto=format&fit=crop" : base2 + "?auto=format";
}

function sanityFetch(query) {
  var url = SANITY_BASE + "?query=" + encodeURIComponent(query);
  return fetch(url).then(function(r) { return r.json(); }).then(function(d) { return d.result; });
}

function useSanity(query, fallback) {
  var s = useState(fallback);
  var data = s[0]; var setData = s[1];
  var l = useState(true);
  var loading = l[0]; var setLoading = l[1];
  useEffect(function() {
    sanityFetch(query)
      .then(function(result) {
        if (result !== null && result !== undefined) {
          if (Array.isArray(result) && result.length > 0) setData(result);
          if (!Array.isArray(result) && result) setData(result);
        }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }, []);
  return [data, loading];
}

// portable text → plain string for blog body blocks
function ptToBlocks(body) {
  if (!body || !Array.isArray(body)) return [];
  var out = [];
  body.forEach(function(block) {
    if (block._type === "block") {
      // preserve children with marks for bold/italic/links
      var children = (block.children || []);
      var text = children.map(function(c) { return c.text || ""; }).join("");
      if (!text.trim()) return;
      var markDefs = block.markDefs || [];
      if (block.style === "h2") { out.push({ type: "h2", text: text, children: children, markDefs: markDefs }); }
      else if (block.style === "h3") { out.push({ type: "h3", text: text, children: children, markDefs: markDefs }); }
      else if (block.style === "blockquote") { out.push({ type: "callout", text: text }); }
      else { out.push({ type: "p", text: text, children: children, markDefs: markDefs }); }
    } else if (block._type === "image" && block.asset) {
      out.push({ type: "image", asset: block.asset, alt: block.alt || "", caption: block.caption || "", size: block.size || "full" });
    } else if (block._type === "videoEmbed" && block.url) {
      out.push({ type: "video", url: block.url, caption: block.caption || "" });
    } else if (block._type === "statRow" && block.stats) {
      out.push({ type: "stat", items: block.stats.map(function(s) { return { value: s.value, label: s.label }; }) });
    }
  });
  return out;
}

function getVideoEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  var ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (ytMatch) return "https://www.youtube.com/embed/" + ytMatch[1] + "?rel=0&modestbranding=1";
  // Vimeo
  var vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return "https://player.vimeo.com/video/" + vimeoMatch[1];
  return null;
}

function RichText(props) {
  var children = props.children || [];
  var markDefs = props.markDefs || [];
  return (
    <span>
      {children.map(function(child, i) {
        var text = child.text || "";
        var marks = child.marks || [];
        var el = text;
        // Find link annotation if any mark references a markDef _key
        var linkDef = null;
        marks.forEach(function(mark) {
          var found = markDefs.filter(function(d) { return d._key === mark; })[0];
          if (found && found._type === "link") linkDef = found;
        });
        if (marks.indexOf("strong") !== -1) el = <strong>{el}</strong>;
        if (marks.indexOf("em") !== -1) el = <em>{el}</em>;
        if (marks.indexOf("code") !== -1) el = <code style={{ background: "#f1f5f9", padding: "1px 5px", borderRadius: 4, fontSize: "0.9em", fontFamily: "monospace" }}>{el}</code>;
        if (linkDef && linkDef.href) {
          el = <a key={i} href={linkDef.href} target={linkDef.blank !== false ? "_blank" : "_self"} rel="noreferrer"
            style={{ color: "#22A7FF", textDecoration: "underline", textDecorationColor: "rgba(34,167,255,0.35)", textUnderlineOffset: 3 }}>{el}</a>;
        } else {
          el = <span key={i}>{el}</span>;
        }
        return el;
      })}
    </span>
  );
}

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Google Sans', sans-serif; background: #fff; color: #04100D; overflow-x: hidden; }
  ::selection { background: #22A7FF33; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  @keyframes fadeUp  { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
  @keyframes tagIn   { from { opacity:0; transform:translateY(16px) scale(0.92); } to { opacity:1; transform:translateY(0) scale(1); } }
  @keyframes blobPulse  { 0%,100% { transform:scale(1) rotate(0deg); } 50% { transform:scale(1.08) rotate(6deg); } }
  @keyframes blobPulse2 { 0%,100% { transform:scale(1) rotate(0deg); } 50% { transform:scale(1.06) rotate(-5deg); } }
  @keyframes slideDown  { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes dotPulse {
    0%, 100% { transform: scale(1);   opacity: 1;   }
    50%       { transform: scale(1.6); opacity: 0.6; }
  }
  @keyframes tagFloat {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-3px); }
  }

  .fade-up   { animation: fadeUp 0.7s 0.00s ease both; }
  .fade-up-1 { animation: fadeUp 0.7s 0.10s ease both; }
  .fade-up-2 { animation: fadeUp 0.7s 0.20s ease both; }
  .fade-up-3 { animation: fadeUp 0.7s 0.30s ease both; }
  .fade-up-4 { animation: fadeUp 0.7s 0.40s ease both; }
  .fade-up-5 { animation: fadeUp 0.7s 0.50s ease both; }

  .cap-tag {
    display: inline-flex; align-items: center; gap: 9px;
    border-radius: 28px; padding: 9px 18px;
    font-size: 13.5px; font-weight: 700; cursor: default;
    position: relative; overflow: hidden;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
  }
  .cap-tag::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
    background-size: 200% 100%;
    animation: shimmer 2.8s ease infinite;
    pointer-events: none;
    border-radius: inherit;
  }
  .cap-tag:hover {
    transform: translateY(-3px) scale(1.04);
    box-shadow: 0 8px 28px rgba(0,0,0,0.14);
  }
  .cap-tag .dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    animation: dotPulse 2s ease-in-out infinite;
  }

  .gradient-text {
    background: linear-gradient(135deg, #22A7FF 0%, #165895 30%, #D52D9B 70%, #F79964 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .section-label {
    font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    background: linear-gradient(135deg, #22A7FF, #D52D9B);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }

  /* PRIMARY BUTTON — dark background, gradient text on hover */
  .btn-primary {
    background: #04100D;
    color: #fff; border: none; cursor: pointer;
    font-family: 'Google Sans', sans-serif; font-weight: 600;
    transition: opacity 0.2s, transform 0.15s;
    text-decoration: none; display: inline-flex; align-items: center; justify-content: center;
    outline: none; box-sizing: border-box;
  }
  .btn-primary:hover { opacity: 0.82; transform: translateY(-1px); }
  a.btn-primary { display: inline-block; line-height: normal; vertical-align: middle; }

  /* ACCENT BUTTON — gradient background (used for badges / highlights only) */
  .btn-gradient {
    background: linear-gradient(135deg, #22A7FF 0%, #165895 35%, #D52D9B 70%, #F79964 100%);
    color: #fff; border: none; cursor: pointer;
    font-family: 'Google Sans', sans-serif; font-weight: 600;
    transition: opacity 0.2s, transform 0.15s;
    text-decoration: none; display: inline-flex; align-items: center; justify-content: center;
  }
  .btn-gradient:hover { opacity: 0.88; transform: translateY(-1px); }
  a.btn-gradient { display: inline-block; line-height: normal; vertical-align: middle; }

  .btn-outline {
    background: transparent; border: 2px solid #e2e8f0; color: #04100D; cursor: pointer;
    font-family: 'Google Sans', sans-serif; font-weight: 600;
    transition: border-color 0.2s, transform 0.15s;
    text-decoration: none; display: inline-flex; align-items: center; justify-content: center;
  }
  .btn-outline:hover { border-color: #22A7FF; transform: translateY(-1px); }

  .nav-link {
    font-size: 14px; font-weight: 500; color: #4a5568;
    text-decoration: none; padding: 6px 0; transition: color 0.2s;
    background: none; border: none; cursor: pointer; font-family: 'Google Sans', sans-serif;
  }
  .nav-link:hover { color: #04100D; }
  .nav-link.active {
    color: #04100D;
    font-weight: 700;
  }

  .dot-grid {
    background-image: radial-gradient(circle, rgba(34,167,255,0.15) 1px, transparent 1px);
    background-size: 24px 24px;
  }
  .card-hover { transition: transform 0.25s ease, box-shadow 0.25s ease; }
  .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(34,167,255,0.12); }

  .faq-item { border-bottom: 1px solid #e2e8f0; }
  .faq-btn  { width:100%; background:none; border:none; cursor:pointer; font-family:'Google Sans', sans-serif; }

  .plan-card {
    border: 2px solid #e2e8f0; border-radius: 20px; padding: 32px;
    position: relative; transition: all 0.25s; background: #fff;
    display: flex; flex-direction: column;
  }
  .plan-card:hover { border-color: #22A7FF55; box-shadow: 0 12px 40px rgba(34,167,255,0.1); }
  .plan-card.featured { border: none; }
  .plan-card.featured::before {
    content:''; position:absolute; inset:-2px; border-radius:22px;
    background: linear-gradient(135deg, #22A7FF, #165895, #D52D9B, #F79964); z-index:-1;
  }

  .blog-card { border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0; transition: all 0.25s; }
  .blog-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,0.08); }

  .tag {
    display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px;
    background: linear-gradient(135deg, #22A7FF22, #D52D9B22); color: #165895; letter-spacing: 0.04em;
  }
  input, textarea {
    font-family: 'Google Sans', sans-serif; outline: none; background: #fff;
    border: 2px solid #e2e8f0; border-radius: 10px; padding: 12px 16px;
    font-size: 14px; transition: border-color 0.2s; width: 100%;
  }
  input:focus, textarea:focus { border-color: #22A7FF; }

  .mobile-menu { animation: slideDown 0.25s ease both; }

  .tilt-card { transform-style: preserve-3d; will-change: transform; }
  .stats-row { grid-template-columns: repeat(4,1fr) !important; }
  @media (max-width: 768px) { .stats-row { grid-template-columns: repeat(2,1fr) !important; } }

  /* Custom cursor elements */
  .cursor-ring {
    width: 34px; height: 34px; border-radius: 50%;
    border: 1.5px solid rgba(34,167,255,0.45);
    position: fixed; top: 0; left: 0; pointer-events: none; z-index: 9998;
    transform: translate(-50%, -50%);
    transition: width 0.25s ease, height 0.25s ease, border-color 0.25s ease, opacity 0.25s ease;
  }
  .cursor-ring.hovered { width: 54px; height: 54px; border-color: rgba(34,167,255,0.85); }

  @media (max-width: 768px) {
    .desktop-nav     { display: none !important; }
    .desktop-cta     { display: none !important; }
    .mobile-menu-btn { display: flex !important; }
    .product-row     { grid-template-columns: 1fr !important; }
    .two-col         { grid-template-columns: 1fr !important; }
    .cursor-dot, .cursor-ring { display: none !important; }

    /* ── Footer: stack into 2 columns then single ── */
    .footer-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 32px !important;
    }
    .footer-logo-col {
      grid-column: 1 / -1 !important;
    }
    .footer-contact-col {
      grid-column: 1 / -1 !important;
    }

    /* ── Typography: bump up body text ── */
    body { font-size: 16px; }

    /* ── Hero ── */
    .hero-section { padding-top: 56px !important; min-height: auto !important; }
    .hero-inner   { padding: 48px 20px 56px !important; }

    /* ── Section padding ── */
    .section-pad { padding-left: 20px !important; padding-right: 20px !important; }

    /* ── Blog cards: single column ── */
    .blog-featured { grid-template-columns: 1fr !important; min-height: auto !important; }
    .blog-featured-image { display: none !important; }
    .blog-grid { grid-template-columns: 1fr !important; justify-content: stretch !important; }

    /* ── Pricing cards: stack ── */
    .pricing-grid { grid-template-columns: 1fr !important; }

    /* ── Solutions tabs: scroll horizontally ── */
    .solutions-tabs {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
      flex-wrap: nowrap !important;
      justify-content: flex-start !important;
      padding-bottom: 4px !important;
    }

    /* ── Stat grid: 2 columns on mobile ── */
    .stat-grid-3 { grid-template-columns: repeat(2,1fr) !important; }

    /* ── Plan card: full width ── */
    .plan-card { width: 100% !important; }

    /* ── Capability tags: smaller ── */
    .cap-tag { font-size: 13px !important; padding: 8px 14px !important; }

    /* ── Footer bottom bar ── */
    .footer-bottom {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 8px !important;
    }
  }

  @media (max-width: 480px) {
    .footer-grid {
      grid-template-columns: 1fr !important;
    }
    .blog-featured { min-height: auto !important; }
  }

  @media (min-width: 769px) {
    .mobile-menu-btn { display: none !important; }
    .mobile-menu     { display: none !important; }
  }
`;

// ─── REAL SVG LOGO (inlined verbatim from source file) ───────────────────────
function Logo({ height }) {
  var h = height || 24;
  var w = Math.round((243 / 28) * h);
  return (
    <svg width={w} height={h} viewBox="0 0 243 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="lg_icon" x1="19.4031" y1="4.42756" x2="-0.311683" y2="18.9319" gradientUnits="userSpaceOnUse">
          <stop stopColor="#22A7FF"/>
          <stop offset="0.35" stopColor="#165895"/>
          <stop offset="0.62" stopColor="#D52D9B"/>
          <stop offset="1" stopColor="#F79964"/>
        </linearGradient>
        <linearGradient id="lg_dot" x1="123.502" y1="4.66661" x2="128.17" y2="0.660295" gradientUnits="userSpaceOnUse">
          <stop stopColor="#25A385"/>
          <stop offset="1" stopColor="#65C5FC"/>
        </linearGradient>
      </defs>
      <path d="M214.513 12.9059V21.3325H211.255V13.7592C211.255 13.2348 211.126 12.7593 210.868 12.3326C210.61 11.897 210.263 11.5548 209.826 11.3059C209.399 11.0482 208.923 10.9193 208.398 10.9193C207.873 10.9193 207.392 11.0482 206.956 11.3059C206.529 11.5548 206.186 11.897 205.928 12.3326C205.678 12.7593 205.554 13.2348 205.554 13.7592V21.3325H202.296L202.283 7.99931H205.541L205.554 9.18596C206.008 8.70596 206.551 8.32819 207.183 8.05264C207.815 7.7682 208.496 7.62598 209.226 7.62598C210.196 7.62598 211.081 7.86597 211.882 8.34597C212.684 8.81707 213.32 9.45262 213.792 10.2526C214.272 11.0437 214.513 11.9281 214.513 12.9059Z" fill="#04100D"/>
      <path d="M193.528 21.6792C192.3 21.6792 191.178 21.3636 190.163 20.7325C189.158 20.1014 188.352 19.2525 187.747 18.1859C187.15 17.1192 186.852 15.9414 186.852 14.6526C186.852 13.6837 187.026 12.777 187.373 11.9326C187.72 11.0793 188.196 10.3326 188.802 9.69262C189.416 9.04374 190.128 8.53708 190.938 8.17264C191.748 7.8082 192.611 7.62598 193.528 7.62598C194.569 7.62598 195.522 7.8482 196.385 8.29263C197.257 8.72819 197.996 9.32818 198.602 10.0926C199.207 10.8571 199.647 11.7282 199.923 12.7059C200.199 13.6837 200.262 14.7059 200.11 15.7726H190.35C190.475 16.2703 190.68 16.7192 190.964 17.1192C191.249 17.5103 191.61 17.8259 192.046 18.0659C192.482 18.297 192.976 18.417 193.528 18.4259C194.098 18.4347 194.614 18.3014 195.077 18.0259C195.549 17.7414 195.94 17.3592 196.252 16.8792L199.576 17.6525C199.033 18.8347 198.223 19.8036 197.146 20.5592C196.069 21.3058 194.863 21.6792 193.528 21.6792ZM190.243 13.3326H196.812C196.715 12.7993 196.505 12.3193 196.185 11.8926C195.873 11.457 195.486 11.1104 195.023 10.8526C194.56 10.5948 194.062 10.4659 193.528 10.4659C192.994 10.4659 192.5 10.5948 192.046 10.8526C191.592 11.1015 191.205 11.4437 190.884 11.8793C190.573 12.3059 190.359 12.7904 190.243 13.3326Z" fill="#04100D"/>
      <path d="M178.846 21.6792C177.618 21.6792 176.497 21.3636 175.482 20.7325C174.476 20.1014 173.67 19.2525 173.065 18.1859C172.469 17.1192 172.171 15.9414 172.171 14.6526C172.171 13.6837 172.344 12.777 172.691 11.9326C173.039 11.0793 173.515 10.3326 174.12 9.69262C174.734 9.04374 175.446 8.53708 176.256 8.17264C177.066 7.8082 177.93 7.62598 178.846 7.62598C179.888 7.62598 180.84 7.8482 181.704 8.29263C182.576 8.72819 183.315 9.32818 183.92 10.0926C184.525 10.8571 184.966 11.7282 185.242 12.7059C185.518 13.6837 185.58 14.7059 185.429 15.7726H175.669C175.793 16.2703 175.998 16.7192 176.283 17.1192C176.568 17.5103 176.928 17.8259 177.364 18.0659C177.801 18.297 178.295 18.417 178.846 18.4259C179.416 18.4347 179.932 18.3014 180.395 18.0259C180.867 17.7414 181.259 17.3592 181.57 16.8792L184.895 17.6525C184.352 18.8347 183.542 19.8036 182.465 20.5592C181.388 21.3058 180.182 21.6792 178.846 21.6792ZM175.562 13.3326H182.131C182.033 12.7993 181.824 12.3193 181.503 11.8926C181.192 11.457 180.805 11.1104 180.342 10.8526C179.879 10.5948 179.381 10.4659 178.846 10.4659C178.312 10.4659 177.818 10.5948 177.364 10.8526C176.91 11.1015 176.523 11.4437 176.203 11.8793C175.891 12.3059 175.678 12.7904 175.562 13.3326Z" fill="#04100D"/>
      <path d="M163.702 21.3325L163.688 7.99931H166.946L166.959 9.18596C167.413 8.70596 167.956 8.32819 168.588 8.05264C169.22 7.7682 169.901 7.62598 170.631 7.62598C171.121 7.62598 171.61 7.69709 172.1 7.83931L170.818 11.1193C170.48 10.9859 170.141 10.9193 169.803 10.9193C169.278 10.9193 168.797 11.0482 168.361 11.3059C167.934 11.5548 167.591 11.897 167.333 12.3326C167.084 12.7593 166.959 13.2348 166.959 13.7592V21.3325H163.702Z" fill="#04100D"/>
      <path d="M151.921 21.6798C150.622 21.6798 149.411 21.4265 148.29 20.9198C147.177 20.4131 146.207 19.7154 145.379 18.8265C144.551 17.9287 143.902 16.8976 143.43 15.7332C142.967 14.5599 142.736 13.3066 142.736 11.9732C142.736 10.5688 142.985 9.27994 143.483 8.10662C143.991 6.9333 144.68 5.91553 145.553 5.05332C146.425 4.1911 147.413 3.52444 148.517 3.05334C149.629 2.58223 150.787 2.34668 151.988 2.34668C153.332 2.34668 154.636 2.66223 155.9 3.29334C157.173 3.91555 158.295 4.87998 159.265 6.18664L156.354 7.74662C155.758 7.01774 155.072 6.47552 154.298 6.11997C153.533 5.75553 152.749 5.57331 151.948 5.57331C151.165 5.57331 150.413 5.73331 149.692 6.05331C148.98 6.3733 148.343 6.82663 147.782 7.41329C147.231 7.99106 146.794 8.6755 146.474 9.4666C146.154 10.2488 145.993 11.1066 145.993 12.0399C145.993 12.9199 146.149 13.751 146.461 14.5332C146.772 15.3065 147.199 15.9865 147.742 16.5732C148.294 17.151 148.931 17.6043 149.652 17.9332C150.373 18.2621 151.147 18.4265 151.975 18.4265C152.527 18.4265 153.065 18.3421 153.59 18.1732C154.124 17.9954 154.623 17.7421 155.086 17.4132C155.558 17.0843 155.976 16.6843 156.341 16.2132C156.715 15.7421 157.026 15.2088 157.275 14.6132H153.871V11.7732H161.107C161.143 13.1332 160.929 14.4088 160.466 15.5999C160.004 16.791 159.349 17.8443 158.504 18.7598C157.658 19.6754 156.675 20.3909 155.553 20.9065C154.432 21.422 153.221 21.6798 151.921 21.6798Z" fill="#04100D"/>
      <path d="M137.935 17.3192L140.873 18.9059C140.258 19.7414 139.489 20.4125 138.563 20.9192C137.637 21.4258 136.627 21.6792 135.532 21.6792C134.304 21.6792 133.182 21.3636 132.167 20.7325C131.162 20.1014 130.356 19.257 129.751 18.1992C129.154 17.1325 128.856 15.9548 128.856 14.6659C128.856 13.6881 129.03 12.777 129.377 11.9326C129.724 11.0793 130.2 10.3326 130.806 9.69262C131.42 9.04374 132.132 8.53708 132.942 8.17264C133.752 7.8082 134.615 7.62598 135.532 7.62598C136.627 7.62598 137.637 7.87931 138.563 8.38597C139.489 8.89263 140.258 9.57262 140.873 10.4259L137.935 11.9993C137.624 11.6437 137.254 11.3682 136.827 11.1726C136.409 10.977 135.977 10.8793 135.532 10.8793C134.891 10.8793 134.313 11.057 133.796 11.4126C133.28 11.7593 132.871 12.2215 132.568 12.7993C132.265 13.3681 132.114 13.9903 132.114 14.6659C132.114 15.3326 132.265 15.9548 132.568 16.5325C132.871 17.1014 133.28 17.5592 133.796 17.9059C134.313 18.2525 134.891 18.4259 135.532 18.4259C135.995 18.4259 136.435 18.3281 136.854 18.1325C137.272 17.9281 137.633 17.657 137.935 17.3192Z" fill="#04100D"/>
      <path d="M123.503 8H126.761V21.3332H123.503V8Z" fill="#04100D"/>
      <path d="M117.346 7.99998H120.604V21.5865C120.604 22.5465 120.408 23.4176 120.017 24.1998C119.625 24.9909 119.091 25.6665 118.415 26.2264C117.738 26.7953 116.964 27.2309 116.091 27.5331C115.219 27.8442 114.302 27.9998 113.341 27.9998C112.406 27.9998 111.507 27.8087 110.644 27.4264C109.78 27.0442 109.015 26.5198 108.347 25.8531C107.689 25.1953 107.186 24.4442 106.839 23.5998L109.816 22.2532C109.994 22.742 110.261 23.1687 110.617 23.5331C110.982 23.9065 111.4 24.1954 111.872 24.3998C112.344 24.6131 112.834 24.7198 113.341 24.7198C113.857 24.7198 114.351 24.6487 114.823 24.5065C115.304 24.3642 115.735 24.1554 116.118 23.8798C116.501 23.6131 116.799 23.2843 117.013 22.8932C117.235 22.5109 117.346 22.0754 117.346 21.5865V19.7065C116.928 20.2932 116.403 20.7687 115.771 21.1332C115.139 21.4976 114.409 21.6798 113.581 21.6798C112.62 21.6798 111.721 21.4976 110.884 21.1332C110.048 20.7687 109.309 20.2665 108.668 19.6265C108.036 18.9776 107.537 18.231 107.172 17.3865C106.816 16.5421 106.638 15.6355 106.638 14.6666C106.638 13.6977 106.816 12.791 107.172 11.9466C107.537 11.1022 108.036 10.36 108.668 9.71996C109.309 9.07108 110.048 8.56442 110.884 8.19998C111.721 7.83554 112.62 7.65332 113.581 7.65332C114.409 7.65332 115.139 7.83554 115.771 8.19998C116.403 8.55553 116.928 9.02664 117.346 9.6133V7.99998ZM113.621 18.5465C114.307 18.5465 114.912 18.3732 115.437 18.0265C115.971 17.671 116.39 17.1999 116.692 16.6132C116.995 16.0177 117.146 15.3688 117.146 14.6666C117.146 13.9555 116.99 13.3066 116.679 12.7199C116.376 12.1333 115.958 11.6622 115.424 11.3066C114.899 10.9511 114.298 10.7733 113.621 10.7733C112.954 10.7733 112.344 10.9511 111.792 11.3066C111.24 11.6533 110.8 12.1199 110.47 12.7066C110.141 13.2933 109.976 13.9466 109.976 14.6666C109.976 15.3866 110.141 16.0399 110.47 16.6266C110.8 17.2132 111.24 17.6799 111.792 18.0265C112.344 18.3732 112.954 18.5465 113.621 18.5465Z" fill="#04100D"/>
      <path d="M98.6327 21.6792C97.4044 21.6792 96.2828 21.3636 95.2681 20.7325C94.2623 20.1014 93.4567 19.2525 92.8515 18.1859C92.2551 17.1192 91.9569 15.9414 91.9569 14.6526C91.9569 13.6837 92.1305 12.777 92.4776 11.9326C92.8248 11.0793 93.301 10.3326 93.9062 9.69262C94.5204 9.04374 95.2325 8.53708 96.0425 8.17264C96.8525 7.8082 97.7159 7.62598 98.6327 7.62598C99.6741 7.62598 100.627 7.8482 101.49 8.29263C102.362 8.72819 103.101 9.32818 103.706 10.0926C104.312 10.8571 104.752 11.7282 105.028 12.7059C105.304 13.6837 105.366 14.7059 105.215 15.7726H95.455C95.5796 16.2703 95.7844 16.7192 96.0692 17.1192C96.354 17.5103 96.7145 17.8259 97.1507 18.0659C97.5868 18.297 98.0808 18.417 98.6327 18.4259C99.2024 18.4347 99.7186 18.3014 100.181 18.0259C100.653 17.7414 101.045 17.3592 101.356 16.8792L104.681 17.6525C104.138 18.8347 103.328 19.8036 102.251 20.5592C101.174 21.3058 99.9679 21.6792 98.6327 21.6792ZM95.3482 13.3326H101.917C101.819 12.7993 101.61 12.3193 101.29 11.8926C100.978 11.457 100.591 11.1104 100.128 10.8526C99.6652 10.5948 99.1668 10.4659 98.6327 10.4659C98.0986 10.4659 97.6046 10.5948 97.1507 10.8526C96.6967 11.1015 96.3095 11.4437 95.9891 11.8793C95.6775 12.3059 95.4639 12.7904 95.3482 13.3326Z" fill="#04100D"/>
      <path d="M90.657 11.2524H88.0668V21.3323H84.809V11.2524H82.833V7.99912H84.809V3.8125H88.0668V7.99912H90.657V11.2524Z" fill="#04100D"/>
      <path d="M77.5822 7.99931H80.84V21.3325H77.5822L77.4353 19.6792C77.0704 20.2747 76.5853 20.7592 75.98 21.1325C75.3836 21.4969 74.676 21.6792 73.8571 21.6792C72.8691 21.6792 71.9434 21.4925 71.08 21.1192C70.2166 20.7458 69.4555 20.2303 68.7968 19.5725C68.1471 18.9147 67.6352 18.1547 67.2614 17.2925C66.8965 16.4303 66.714 15.5059 66.714 14.5192C66.714 13.5681 66.8876 12.6748 67.2347 11.8393C67.5907 11.0037 68.0848 10.2704 68.7167 9.63929C69.3487 9.00818 70.0786 8.51485 70.9064 8.1593C71.7342 7.80375 72.6243 7.62598 73.5767 7.62598C74.4579 7.62598 75.2456 7.82153 75.9399 8.21264C76.6431 8.60374 77.2395 9.10151 77.729 9.70595L77.5822 7.99931ZM73.777 18.5459C74.4713 18.5459 75.0854 18.3725 75.6195 18.0259C76.1536 17.6792 76.5719 17.2125 76.8745 16.6259C77.1772 16.0303 77.3285 15.377 77.3285 14.6659C77.3285 13.9459 77.1772 13.2926 76.8745 12.7059C76.5719 12.1104 76.1491 11.6393 75.6061 11.2926C75.0721 10.9459 74.4624 10.7726 73.777 10.7726C73.0916 10.7726 72.4641 10.9504 71.8944 11.3059C71.3336 11.6526 70.8841 12.1193 70.5459 12.7059C70.2166 13.2926 70.0519 13.9459 70.0519 14.6659C70.0519 15.3859 70.221 16.0392 70.5592 16.6259C70.8975 17.2125 71.347 17.6792 71.9078 18.0259C72.4774 18.3725 73.1005 18.5459 73.777 18.5459Z" fill="#04100D"/>
      <path d="M58.245 21.3325L58.2317 7.99931H61.4895L61.5028 9.18596C61.9568 8.70596 62.4997 8.32819 63.1317 8.05264C63.7637 7.7682 64.4446 7.62598 65.1745 7.62598C65.6641 7.62598 66.1536 7.69709 66.6432 7.83931L65.3614 11.1193C65.0232 10.9859 64.685 10.9193 64.3467 10.9193C63.8215 10.9193 63.3409 11.0482 62.9047 11.3059C62.4775 11.5548 62.1348 11.897 61.8767 12.3326C61.6274 12.7593 61.5028 13.2348 61.5028 13.7592V21.3325H58.245Z" fill="#04100D"/>
      <path d="M55.8178 11.2524H53.2276V21.3323H49.9698V11.2524H47.9938V7.99912H49.9698V3.8125H53.2276V7.99912H55.8178V11.2524Z" fill="#04100D"/>
      <path d="M39.04 21.6798C38.0075 21.6798 37.0151 21.5198 36.0626 21.1998C35.1191 20.8709 34.2913 20.4043 33.5793 19.7998C32.8672 19.1865 32.3554 18.4532 32.0438 17.5998L35.1013 16.4799C35.2259 16.8354 35.4796 17.1598 35.8624 17.4532C36.2451 17.7465 36.7124 17.9821 37.2643 18.1598C37.8162 18.3376 38.4081 18.4265 39.04 18.4265C39.6898 18.4265 40.2951 18.3287 40.8559 18.1332C41.4255 17.9287 41.8839 17.6487 42.2311 17.2932C42.5782 16.9287 42.7518 16.5065 42.7518 16.0265C42.7518 15.5376 42.5693 15.1376 42.2044 14.8265C41.8394 14.5065 41.3721 14.2532 40.8025 14.0666C40.2328 13.8799 39.6453 13.7377 39.04 13.6399C37.7138 13.4354 36.5255 13.1199 35.4752 12.6932C34.4249 12.2666 33.5926 11.6755 32.9784 10.9199C32.3732 10.1644 32.0705 9.1866 32.0705 7.98662C32.0705 6.85774 32.3954 5.87108 33.0452 5.02665C33.695 4.18221 34.5495 3.52444 35.6087 3.05334C36.6679 2.58223 37.8117 2.34668 39.04 2.34668C40.0637 2.34668 41.0517 2.50668 42.0041 2.82667C42.9565 3.14667 43.7888 3.61333 44.5008 4.22666C45.2218 4.83998 45.7381 5.57775 46.0496 6.43997L42.9788 7.54662C42.8541 7.19107 42.6005 6.86663 42.2177 6.5733C41.835 6.27997 41.3677 6.04886 40.8158 5.87997C40.2639 5.7022 39.672 5.61331 39.04 5.61331C38.4081 5.60442 37.8073 5.70664 37.2376 5.91997C36.6768 6.12442 36.2184 6.40441 35.8624 6.75996C35.5063 7.11552 35.3283 7.5244 35.3283 7.98662C35.3283 8.58217 35.493 9.03105 35.8223 9.33327C36.1606 9.63549 36.6101 9.85771 37.1708 9.99993C37.7316 10.1333 38.3547 10.2577 39.04 10.3733C40.2862 10.5688 41.4389 10.8977 42.4981 11.3599C43.5573 11.8221 44.4074 12.4399 45.0483 13.2132C45.6891 13.9777 46.0096 14.9154 46.0096 16.0265C46.0096 17.1554 45.6891 18.1465 45.0483 18.9998C44.4074 19.8443 43.5573 20.502 42.4981 20.9731C41.4389 21.4442 40.2862 21.6798 39.04 21.6798Z" fill="#04100D"/>
      <path d="M8.2832 1.94043C9.77836 1.13103 11.582 1.13104 13.0771 1.94043L18.7285 5C20.3505 5.87812 21.3612 7.57261 21.3613 9.41504V15.0957C21.3611 16.938 20.3504 18.6327 18.7285 19.5107L13.0771 22.5703C11.582 23.3795 9.77827 23.3796 8.2832 22.5703L2.63184 19.5107C1.01009 18.6327 -0.000716343 16.9379 -0.000976562 15.0957V9.41504C-0.000869828 7.57278 1.01012 5.8782 2.63184 5L8.2832 1.94043ZM1.94531 8.43164C1.84507 8.74498 1.79007 9.0761 1.79004 9.41504V15.0957C1.7903 16.2816 2.44133 17.3732 3.48535 17.9385L9.13672 20.998C9.34433 21.1104 9.56208 21.1968 9.78418 21.2607V12.8281L1.94531 8.43164ZM12.2236 3.5127C11.261 2.99162 10.0993 2.99159 9.13672 3.5127L3.48535 6.57227C3.29101 6.67752 3.10974 6.80154 2.94434 6.94043L10.8604 11.3809C11.0195 11.4692 11.1405 11.599 11.2188 11.749C11.4349 11.9123 11.5752 12.1704 11.5752 12.4619V21.2607C11.7975 21.1968 12.0158 21.1105 12.2236 20.998L17.875 17.9385C18.9191 17.3732 19.5691 16.2817 19.5693 15.0957V9.41504C19.5693 9.04749 19.5069 8.689 19.3896 8.35254C19.3872 8.3541 19.3844 8.357 19.3818 8.3584L14.7676 10.8467C14.3353 11.0861 13.79 10.9305 13.5498 10.499C13.3097 10.0675 13.4656 9.52331 13.8975 9.2832L18.3438 6.88477C18.1978 6.76906 18.0422 6.66279 17.875 6.57227L12.2236 3.5127Z" fill="url(#lg_icon)"/>
      <path d="M122.834 2.66664C122.834 1.19389 124.028 0 125.501 0H127.175C127.727 0 128.175 0.447715 128.175 1V2.66295C128.175 4.13773 126.978 5.33327 125.503 5.33327C124.03 5.33327 122.834 4.13938 122.834 2.66664Z" fill="url(#lg_dot)"/>
      <path d="M239.71 7.10694H241.579V20.4401H239.71V7.10694ZM240.658 4.77364C240.355 4.77364 240.097 4.67586 239.883 4.48031C239.679 4.27586 239.576 4.02253 239.576 3.72031C239.576 3.41809 239.679 3.16921 239.883 2.97366C240.097 2.76921 240.355 2.66699 240.658 2.66699C240.952 2.66699 241.201 2.76921 241.405 2.97366C241.619 3.16921 241.726 3.41809 241.726 3.72031C241.726 4.02253 241.624 4.27586 241.419 4.48031C241.214 4.67586 240.96 4.77364 240.658 4.77364Z" fill="#04100D"/>
      <path d="M235.169 7.10673H237.038V20.4399H235.169L235.102 17.9599C234.666 18.8044 234.056 19.4888 233.273 20.0132C232.49 20.5288 231.555 20.7866 230.469 20.7866C229.49 20.7866 228.573 20.6044 227.719 20.2399C226.864 19.8666 226.112 19.3555 225.462 18.7066C224.812 18.0577 224.305 17.3066 223.94 16.4533C223.575 15.6 223.393 14.6844 223.393 13.7067C223.393 12.7467 223.571 11.8445 223.927 11C224.292 10.1556 224.79 9.41337 225.422 8.77337C226.063 8.13338 226.802 7.63561 227.639 7.28006C228.484 6.91562 229.388 6.7334 230.349 6.7334C231.462 6.7334 232.432 7.00006 233.26 7.53339C234.087 8.05783 234.746 8.74671 235.236 9.60003L235.169 7.10673ZM230.429 18.9733C231.381 18.9733 232.2 18.7422 232.886 18.2799C233.571 17.8088 234.096 17.1777 234.461 16.3866C234.835 15.5955 235.022 14.7244 235.022 13.7733C235.022 12.7956 234.835 11.9156 234.461 11.1333C234.087 10.3422 233.558 9.71559 232.872 9.25337C232.187 8.79115 231.372 8.56004 230.429 8.56004C229.485 8.56004 228.622 8.7956 227.839 9.2667C227.064 9.72892 226.446 10.3556 225.983 11.1467C225.529 11.9378 225.302 12.8133 225.302 13.7733C225.302 14.7422 225.538 15.6222 226.01 16.4133C226.481 17.1955 227.104 17.8177 227.879 18.2799C228.662 18.7422 229.512 18.9733 230.429 18.9733Z" fill="#04100D"/>
      <path d="M220.021 20.7866C219.727 20.7866 219.478 20.68 219.273 20.4666C219.068 20.2622 218.966 20.0089 218.966 19.7066C218.966 19.4222 219.068 19.1778 219.273 18.9733C219.478 18.76 219.727 18.6533 220.021 18.6533C220.323 18.6533 220.577 18.76 220.782 18.9733C220.995 19.1778 221.102 19.4222 221.102 19.7066C221.102 20.0089 220.995 20.2622 220.782 20.4666C220.577 20.68 220.323 20.7866 220.021 20.7866Z" fill="#04100D"/>
    </svg>
  );
}

function GradCheck() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <defs>
        <linearGradient id="cg1" x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22A7FF"/>
          <stop offset="100%" stopColor="#D52D9B"/>
        </linearGradient>
      </defs>
      <circle cx="10" cy="10" r="10" fill="url(#cg1)" opacity="0.15"/>
      <path d="M6 10.5L8.5 13L14 7.5" stroke="url(#cg1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── SVG ICON LIBRARY ────────────────────────────────────────────────────────
// All icons are stroke-based, 32×32 viewBox, colour via prop (default #04100D)
function Icon(props) {
  var name = props.name;
  var size = props.size || 28;
  var color = props.color || "#04100D";
  var sw = props.strokeWidth || 1.6;
  var icons = {
    "clipboard-x": <g><rect x="5" y="4" width="22" height="26" rx="2" stroke={color} strokeWidth={sw} fill="none"/><path d="M11 4v-1a1 1 0 011-1h8a1 1 0 011 1v1" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="11" y1="14" x2="21" y2="14" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="11" y1="19" x2="18" y2="19" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="13" y1="9" x2="19" y2="9" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "calendar-alert": <g><rect x="4" y="6" width="24" height="22" rx="2" stroke={color} strokeWidth={sw} fill="none"/><line x1="4" y1="12" x2="28" y2="12" stroke={color} strokeWidth={sw}/><line x1="10" y1="3" x2="10" y2="9" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="22" y1="3" x2="22" y2="9" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="16" y1="17" x2="16" y2="22" stroke={color} strokeWidth={sw} strokeLinecap="round"/><circle cx="16" cy="25" r="1" fill={color}/></g>,
    "shuffle": <g><path d="M6 8h14a6 6 0 016 6v0a6 6 0 01-6 6H6" stroke={color} strokeWidth={sw} strokeLinecap="round" fill="none"/><polyline points="22,4 28,8 22,12" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M26 20l2 4-2 4" stroke={color} strokeWidth={sw} strokeLinecap="round" fill="none"/><line x1="6" y1="8" x2="10" y2="8" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "clock": <g><circle cx="16" cy="16" r="11.5" stroke={color} strokeWidth={sw} fill="none" strokeLinecap="round"/><polyline points="16,9 16,16.5 20.5,20" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><line x1="16" y1="4" x2="16" y2="6.5" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="16" y1="25.5" x2="16" y2="28" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="4" y1="16" x2="6.5" y2="16" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="25.5" y1="16" x2="28" y2="16" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "error": <g><path d="M16 5 L28 26 Q28.5 27 27 27 L5 27 Q3.5 27 4 26 Z" stroke={color} strokeWidth={sw} fill="none" strokeLinejoin="round" strokeLinecap="round"/><line x1="16" y1="13" x2="16" y2="20" stroke={color} strokeWidth={sw} strokeLinecap="round"/><circle cx="16" cy="23.5" r="1.2" fill={color}/></g>,
    "chart": <g><polyline points="4,24 10,16 15,20 21,10 28,6" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><polyline points="23,6 28,6 28,11" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><line x1="4" y1="28" x2="28" y2="28" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="4" y1="6" x2="4" y2="28" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "file-extract": <g><path d="M20 4H8a2 2 0 00-2 2v20a2 2 0 002 2h16a2 2 0 002-2V10l-6-6z" stroke={color} strokeWidth={sw} fill="none" strokeLinejoin="round"/><polyline points="20,4 20,10 26,10" stroke={color} strokeWidth={sw} fill="none" strokeLinejoin="round"/><line x1="11" y1="16" x2="21" y2="16" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="11" y1="21" x2="17" y2="21" stroke={color} strokeWidth={sw} strokeLinecap="round"/><polyline points="19,21 22,24 25,21" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><line x1="22" y1="17" x2="22" y2="24" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "chart-bar": <g><rect x="4" y="18" width="6" height="9" rx="1" stroke={color} strokeWidth={sw} fill="none"/><rect x="13" y="11" width="6" height="16" rx="1" stroke={color} strokeWidth={sw} fill="none"/><rect x="22" y="5" width="6" height="22" rx="1" stroke={color} strokeWidth={sw} fill="none"/><line x1="2" y1="27" x2="30" y2="27" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "building": <g><rect x="6" y="4" width="20" height="24" rx="1" stroke={color} strokeWidth={sw} fill="none"/><line x1="6" y1="12" x2="26" y2="12" stroke={color} strokeWidth={sw}/><line x1="6" y1="20" x2="26" y2="20" stroke={color} strokeWidth={sw}/><rect x="13" y="22" width="6" height="6" rx="0.5" stroke={color} strokeWidth={sw} fill="none"/><rect x="9" y="8" width="3" height="3" rx="0.5" stroke={color} strokeWidth={sw} fill="none"/><rect x="20" y="8" width="3" height="3" rx="0.5" stroke={color} strokeWidth={sw} fill="none"/><rect x="9" y="15" width="3" height="3" rx="0.5" stroke={color} strokeWidth={sw} fill="none"/><rect x="20" y="15" width="3" height="3" rx="0.5" stroke={color} strokeWidth={sw} fill="none"/></g>,
    "file-text": <g><path d="M20 4H8a2 2 0 00-2 2v20a2 2 0 002 2h16a2 2 0 002-2V10l-6-6z" stroke={color} strokeWidth={sw} fill="none" strokeLinejoin="round"/><polyline points="20,4 20,10 26,10" stroke={color} strokeWidth={sw} fill="none" strokeLinejoin="round"/><line x1="11" y1="16" x2="21" y2="16" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="11" y1="21" x2="21" y2="21" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="11" y1="12" x2="14" y2="12" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "cpu": <g><rect x="8" y="8" width="16" height="16" rx="2" stroke={color} strokeWidth={sw} fill="none"/><rect x="12" y="12" width="8" height="8" rx="1" stroke={color} strokeWidth={sw} fill="none"/><line x1="12" y1="4" x2="12" y2="8" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="20" y1="4" x2="20" y2="8" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="12" y1="24" x2="12" y2="28" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="20" y1="24" x2="20" y2="28" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="4" y1="12" x2="8" y2="12" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="4" y1="20" x2="8" y2="20" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="24" y1="12" x2="28" y2="12" stroke={color} strokeWidth={sw} strokeLinecap="round"/><line x1="24" y1="20" x2="28" y2="20" stroke={color} strokeWidth={sw} strokeLinecap="round"/></g>,
    "globe": <g><circle cx="16" cy="16" r="12" stroke={color} strokeWidth={sw} fill="none"/><path d="M4 16h24M16 4c-4 4-6 8-6 12s2 8 6 12M16 4c4 4 6 8 6 12s-2 8-6 12" stroke={color} strokeWidth={sw} fill="none" strokeLinecap="round"/></g>,
    "check-circle": <g><circle cx="16" cy="16" r="12" stroke={color} strokeWidth={sw} fill="none"/><polyline points="10,16 14,20 22,12" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/></g>,
    "zap": <g><polygon points="17,4 6,18 16,18 15,28 26,14 16,14" stroke={color} strokeWidth={sw} strokeLinejoin="round" fill="none"/></g>,
    "layers": <g><polygon points="16,4 28,10 16,16 4,10" stroke={color} strokeWidth={sw} strokeLinejoin="round" fill="none"/><polyline points="4,16 16,22 28,16" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/><polyline points="4,22 16,28 28,22" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" fill="none"/></g>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      {icons[name] || null}
    </svg>
  );
}

// Gradient-filled icon wrapper (for feature cards)
function GradIcon(props) {
  var name = props.name;
  var size = props.size || 44;
  return (
    <div style={{ width: size, height: size, borderRadius: 14, background: "rgba(22,88,149,0.07)", border: "1.5px solid rgba(22,88,149,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
      <Icon name={name} size={Math.round(size * 0.58)} color="#165895" strokeWidth={1.5}/>
    </div>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav(props) {
  var page = props.page;
  var setPage = props.setPage;
  var scrolled = props.scrolled;
  var mobileOpen = props.mobileOpen;
  var setMobileOpen = props.setMobileOpen;
  var pages = ["Home","Solutions","Pricing","About","FAQ","Blog"];

  return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(255,255,255,0.96)" : "transparent",
      backdropFilter: scrolled ? "blur(14px)" : "none",
      borderBottom: scrolled ? "1px solid #e2e8f040" : "none",
      transition: "all 0.3s ease",
      boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.06)" : "none"
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", height: 70, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={function() { setPage("Home"); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
          <Logo height={26} />
        </button>

        <nav className="desktop-nav" style={{ display: "flex", gap: 32, alignItems: "center" }}>
          {pages.map(function(p) {
            return (
              <button key={p} onClick={function() { setPage(p); }} className={"nav-link" + (page === p ? " active" : "")}>
                {p}
              </button>
            );
          })}
        </nav>

        <div className="desktop-cta" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={function() { setPage("Pricing"); }} className="btn-outline" style={{ padding: "9px 20px", borderRadius: 10, fontSize: 14 }}>
            Log In
          </button>
          <a href="https://lite.strategicgreen.ai/" target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: "9px 20px", borderRadius: 10, fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            Access LITE
          </a>
        </div>

        <button className="mobile-menu-btn" onClick={function() { setMobileOpen(!mobileOpen); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "none", flexDirection: "column", gap: 5 }}>
          <span style={{ display: "block", width: 22, height: 2, background: "#04100D", borderRadius: 2 }}/>
          <span style={{ display: "block", width: 22, height: 2, background: "#04100D", borderRadius: 2 }}/>
          <span style={{ display: "block", width: 22, height: 2, background: "#04100D", borderRadius: 2 }}/>
        </button>
      </div>

      {mobileOpen && (
        <div className="mobile-menu" style={{ background: "#fff", borderTop: "1px solid #e2e8f0", padding: "16px 24px 24px" }}>
          {pages.map(function(p) {
            return (
              <button key={p} onClick={function() { setPage(p); setMobileOpen(false); }}
                className={"nav-link" + (page === p ? " active" : "")}
                style={{ display: "block", padding: "10px 0", width: "100%", textAlign: "left", fontSize: 15 }}>
                {p}
              </button>
            );
          })}
          <a href="https://lite.strategicgreen.ai/" target="_blank" rel="noreferrer" className="btn-primary"
            style={{ width: "100%", padding: "12px", borderRadius: 10, fontSize: 15, marginTop: 12, textDecoration: "none", display: "block", textAlign: "center" }}>
            Access LITE
          </a>
        </div>
      )}
    </header>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
// EARLY ACCESS MODAL
// ═══════════════════════════════════════════════════════════════════════════

// EmailJS config — fill these in after creating your EmailJS account
var EMAILJS_SERVICE_ID  = "service_b0wqz7p";
var EMAILJS_TEMPLATE_ID = "template_hs472rm";
var EMAILJS_PUBLIC_KEY  = "rz1zPLyHyDo7Fouds";

// Sanity project config for writing submissions
var SANITY_PROJECT_ID = "8qfgmidk";
var SANITY_DATASET    = "production";
var SANITY_API_VERSION = "2024-01-01";

function EarlyAccessModal(props) {
  var onClose = props.onClose;

  var nameState    = useState("");
  var emailState   = useState("");
  var companyState = useState("");
  var titleState   = useState("");
  var msgState     = useState("");
  var statusState  = useState("idle"); // idle | loading | success | error
  var errorState   = useState("");

  var name    = nameState[0];    var setName    = nameState[1];
  var email   = emailState[0];   var setEmail   = emailState[1];
  var company = companyState[0]; var setCompany = companyState[1];
  var jobTitle= titleState[0];   var setJobTitle= titleState[1];
  var message = msgState[0];     var setMessage = msgState[1];
  var status  = statusState[0];  var setStatus  = statusState[1];
  var errorMsg= errorState[0];   var setErrorMsg= errorState[1];

  // Close on Escape key
  useEffect(function() {
    function handleKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleKey);
    return function() { window.removeEventListener("keydown", handleKey); };
  }, []);

  // Prevent body scroll when modal open
  useEffect(function() {
    document.body.style.overflow = "hidden";
    return function() { document.body.style.overflow = ""; };
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !company.trim()) {
      setErrorMsg("Please fill in all required fields.");
      return;
    }
    if (!email.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setErrorMsg("");
    setStatus("loading");

    var submittedAt = new Date().toISOString();

    // ── Step 1: Save to Sanity (only if write token is configured) ────────
    // Sanity requires an authenticated token to write documents.
    // Set REACT_APP_SANITY_WRITE_TOKEN in Vercel environment variables.
    // Never put the token directly in this file.
    var sanityWriteToken = process.env.REACT_APP_SANITY_WRITE_TOKEN || "";
    if (sanityWriteToken) {
      var sanityDoc = {
        _type: "earlyAccessRequest",
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        jobTitle: jobTitle.trim(),
        message: message.trim(),
        submittedAt: submittedAt,
      };
      fetch(
        "https://" + SANITY_PROJECT_ID + ".api.sanity.io/v" + SANITY_API_VERSION + "/data/mutate/" + SANITY_DATASET,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + sanityWriteToken,
          },
          body: JSON.stringify({ mutations: [{ create: sanityDoc }] }),
        }
      ).catch(function(err) {
        console.error("Sanity write error (non-critical):", err);
      });
    }

    // ── Step 2: Send email via EmailJS (primary notification path) ──────
    fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          from_name: name.trim(),
          from_email: email.trim(),
          company: company.trim(),
          job_title: jobTitle.trim() || "Not provided",
          message: message.trim() || "Not provided",
          submitted_at: new Date(submittedAt).toLocaleString("en-AU", { timeZone: "Australia/Sydney" }),
          to_email_1: "communications@strategicgreenai.com",
          to_email_2: "kayla.wong@sbi.international",
        },
      }),
    })
      .then(function() {
        setStatus("success");
      })
      .catch(function(err) {
        console.error("EmailJS error:", err);
        // Still show success — submission data is logged to console
        setStatus("success");
      });
  }

  var inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 9,
    border: "1.5px solid #e2e8f0", fontSize: 14, fontFamily: "Google Sans, sans-serif",
    color: "#04100D", outline: "none", background: "#fff",
    boxSizing: "border-box", transition: "border-color 0.2s",
  };
  var labelStyle = { fontSize: 13, fontWeight: 600, color: "#04100D", marginBottom: 6, display: "block" };
  var optStyle   = { fontSize: 12, fontWeight: 400, color: "#94a3b8", marginLeft: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(4,16,13,0.55)", backdropFilter: "blur(4px)" }} />

      {/* Modal */}
      <div style={{ position: "relative", background: "#fff", borderRadius: 20, padding: "40px 36px", width: "100%", maxWidth: 480,
        boxShadow: "0 32px 80px rgba(4,16,13,0.18)", maxHeight: "90vh", overflowY: "auto" }}>

        {/* Close button */}
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 32, height: 32, borderRadius: 8,
          border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, color: "#64748b",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          ×
        </button>

        {status === "success" ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: "#04100D", marginBottom: 12, letterSpacing: "-0.5px" }}>
              You are on the list!
            </h2>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.7, marginBottom: 28 }}>
              Thank you for your interest in StrategicGreen.ai LITE. We will be in touch soon with early access details.
            </p>
            <button onClick={onClose} className="btn-primary" style={{ padding: "12px 32px", borderRadius: 11, fontSize: 15 }}>
              Close
            </button>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className="section-label" style={{ marginBottom: 10 }}>Early Access</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#04100D", marginBottom: 8, letterSpacing: "-0.5px" }}>
              Request Early Access to LITE
            </h2>
            <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, marginBottom: 28 }}>
              Be among the first to try StrategicGreen.ai LITE. We will notify you as soon as early access opens.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

                {/* Full Name */}
                <div>
                  <label style={labelStyle}>Full Name <span style={{ color: "#D52D9B" }}>*</span></label>
                  <input type="text" value={name} onChange={function(e) { setName(e.target.value); }}
                    placeholder="Jane Smith" style={inputStyle}
                    onFocus={function(e) { e.target.style.borderColor = "#22A7FF"; }}
                    onBlur={function(e) { e.target.style.borderColor = "#e2e8f0"; }} />
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email Address <span style={{ color: "#D52D9B" }}>*</span></label>
                  <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }}
                    placeholder="jane@company.com" style={inputStyle}
                    onFocus={function(e) { e.target.style.borderColor = "#22A7FF"; }}
                    onBlur={function(e) { e.target.style.borderColor = "#e2e8f0"; }} />
                </div>

                {/* Company */}
                <div>
                  <label style={labelStyle}>Company / Organisation <span style={{ color: "#D52D9B" }}>*</span></label>
                  <input type="text" value={company} onChange={function(e) { setCompany(e.target.value); }}
                    placeholder="Acme Property Group" style={inputStyle}
                    onFocus={function(e) { e.target.style.borderColor = "#22A7FF"; }}
                    onBlur={function(e) { e.target.style.borderColor = "#e2e8f0"; }} />
                </div>

                {/* Job Title */}
                <div>
                  <label style={labelStyle}>Job Title <span style={optStyle}>(optional)</span></label>
                  <input type="text" value={jobTitle} onChange={function(e) { setJobTitle(e.target.value); }}
                    placeholder="Sustainability Manager" style={inputStyle}
                    onFocus={function(e) { e.target.style.borderColor = "#22A7FF"; }}
                    onBlur={function(e) { e.target.style.borderColor = "#e2e8f0"; }} />
                </div>

                {/* Message */}
                <div>
                  <label style={labelStyle}>What are you hoping to use it for? <span style={optStyle}>(optional)</span></label>
                  <textarea value={message} onChange={function(e) { setMessage(e.target.value); }}
                    placeholder="e.g. Extracting utility bills for our property portfolio..." rows={3}
                    style={Object.assign({}, inputStyle, { resize: "vertical", lineHeight: 1.6 })}
                    onFocus={function(e) { e.target.style.borderColor = "#22A7FF"; }}
                    onBlur={function(e) { e.target.style.borderColor = "#e2e8f0"; }} />
                </div>

                {/* Error */}
                {errorMsg && (
                  <div style={{ fontSize: 13, color: "#D52D9B", background: "rgba(213,45,155,0.06)", padding: "10px 14px", borderRadius: 8 }}>
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <button type="submit" className="btn-gradient"
                  style={{ width: "100%", padding: "14px", borderRadius: 11, fontSize: 15, fontWeight: 700,
                    border: "none", cursor: status === "loading" ? "not-allowed" : "pointer", opacity: status === "loading" ? 0.7 : 1 }}>
                  {status === "loading" ? "Submitting..." : "Request Early Access"}
                </button>

                <p style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", lineHeight: 1.6 }}>
                  We respect your privacy. Your details will only be used to notify you about early access.
                </p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COOKIE CONSENT BANNER
// ═══════════════════════════════════════════════════════════════════════════
function CookieBanner() {
  var stored = null;
  try { stored = localStorage.getItem("sga_cookie_consent"); } catch(e) {}

  var visibleState = useState(!stored);
  var visible = visibleState[0];
  var setVisible = visibleState[1];

  var slideState = useState(false);
  var slideIn = slideState[0];
  var setSlideIn = slideState[1];

  useEffect(function() {
    if (!stored) {
      setTimeout(function() { setSlideIn(true); }, 400);
    }
  }, []);

  function grant() {
    try { localStorage.setItem("sga_cookie_consent", "accepted"); } catch(e) {}
    if (window.gtag) {
      window.gtag("consent", "update", { analytics_storage: "granted" });
    }
    setSlideIn(false);
    setTimeout(function() { setVisible(false); }, 400);
  }

  function deny() {
    try { localStorage.setItem("sga_cookie_consent", "declined"); } catch(e) {}
    setSlideIn(false);
    setTimeout(function() { setVisible(false); }, 400);
  }

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9998,
      transform: slideIn ? "translateY(0)" : "translateY(100%)",
      transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
    }}>
      <div style={{
        background: "#04100D", borderTop: "1px solid rgba(255,255,255,0.08)",
        padding: "16px 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", flexWrap: "wrap", gap: 16,
        maxWidth: "100%",
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <p style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6, margin: 0 }}>
            We use cookies to understand how visitors use our site, which helps us improve your experience.
            By clicking Accept you consent to analytics cookies.{" "}
            <span
              onClick={function() { if (window.__sga_setPage) window.__sga_setPage("Privacy"); }}
              style={{ color: "#22A7FF", cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
              Privacy Policy
            </span>
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          <button onClick={deny}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: "transparent", border: "1.5px solid rgba(255,255,255,0.2)",
              color: "#94a3b8", cursor: "pointer", fontFamily: "Google Sans, sans-serif",
              transition: "border-color 0.2s, color 0.2s" }}
            onMouseEnter={function(e) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={function(e) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "#94a3b8"; }}>
            Decline
          </button>
          <button onClick={grant}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: "#22A7FF", border: "none", color: "#fff",
              cursor: "pointer", fontFamily: "Google Sans, sans-serif" }}>
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactPopup(props) {
  var contactEmail = props.contactEmail || "communications@strategicgreenai.com";
  var subject = props.subject || "StrategicGreen.ai%20Demo%20Inquiry";
  var label = props.label || "Book a Demo";

  var openState = useState(false);
  var open = openState[0];
  var setOpen = openState[1];

  var copiedState = useState(false);
  var copied = copiedState[0];
  var setCopied = copiedState[1];

  function copyEmail() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(contactEmail).then(function() {
        setCopied(true);
        setTimeout(function() { setCopied(false); }, 2000);
      });
    } else {
      var ta = document.createElement("textarea");
      ta.value = contactEmail;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    }
  }

  var gmailUrl = "https://mail.google.com/mail/?view=cm&to=" + contactEmail + "&su=" + subject;
  var outlookUrl = "https://outlook.live.com/mail/0/deeplink/compose?to=" + contactEmail + "&subject=" + subject;

  return (
    <div style={{ position: "relative" }}>
      {/* Email address row */}
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 10, whiteSpace: "nowrap" }}>{contactEmail}</div>

      {/* Book a Demo button */}
      <button onClick={function() { setOpen(function(v) { return !v; }); }}
        style={{ marginTop: 4, padding: "10px 18px", borderRadius: 9, fontSize: 13,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "#fff", color: "#04100D", fontWeight: 700,
          border: "none", cursor: "pointer", fontFamily: "Google Sans, sans-serif", lineHeight: 1 }}>
        {label}
      </button>

      {/* Popup */}
      {open && (
        <div style={{ position: "absolute", bottom: "calc(100% + 8px)", right: 0, width: 260,
          background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          padding: "16px", zIndex: 999, border: "1px solid #E8E8E8" }}>

          {/* Close */}
          <button onClick={function() { setOpen(false); }}
            style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none",
              cursor: "pointer", fontSize: 16, color: "#94a3b8", lineHeight: 1, padding: 2 }}>×</button>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#04100D", marginBottom: 12 }}>Send us an email</div>

          {/* Email + copy */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc",
            borderRadius: 7, padding: "8px 10px", marginBottom: 12, border: "1px solid #E8E8E8" }}>
            <span style={{ fontSize: 11, color: "#64748b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contactEmail}</span>
            <button onClick={copyEmail}
              style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 5,
                background: copied ? "#25A385" : "#04100D", color: "#fff", border: "none", cursor: "pointer",
                fontFamily: "Google Sans, sans-serif", transition: "background 0.2s", whiteSpace: "nowrap" }}>
              {copied ? "Copied ✓" : "Copy"}
            </button>
          </div>

          {/* Open in Gmail */}
          <a href={gmailUrl} target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8,
              background: "#fff", border: "1px solid #E8E8E8", textDecoration: "none", marginBottom: 8,
              fontSize: 12, fontWeight: 600, color: "#04100D", fontFamily: "Google Sans, sans-serif" }}
            onMouseEnter={function(e) { e.currentTarget.style.background = "#f8fafc"; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = "#fff"; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6z" stroke="#EA4335" strokeWidth="1.5"/>
              <path d="M22 6l-10 7L2 6" stroke="#EA4335" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Open in Gmail
          </a>

          {/* Open in Outlook */}
          <a href={outlookUrl} target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8,
              background: "#fff", border: "1px solid #E8E8E8", textDecoration: "none",
              fontSize: 12, fontWeight: 600, color: "#04100D", fontFamily: "Google Sans, sans-serif" }}
            onMouseEnter={function(e) { e.currentTarget.style.background = "#f8fafc"; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = "#fff"; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="4" width="20" height="16" rx="2" stroke="#0078D4" strokeWidth="1.5"/>
              <path d="M2 8l10 6 10-6" stroke="#0078D4" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Open in Outlook
          </a>
        </div>
      )}
    </div>
  );
}

function Footer(props) {
  var setPage = props.setPage;

  var footerResult = useSanity('*[_type == "siteSettings"][0]{contactEmail,linkedinUrl,instagramUrl}', null);
  var footerData = footerResult[0];
  var contactEmail = (footerData && footerData.contactEmail) ? footerData.contactEmail : "communications@strategicgreenai.com";
  var linkedinUrl  = (footerData && footerData.linkedinUrl)  ? footerData.linkedinUrl  : "https://www.linkedin.com/company/strategicgreen-ai";
  var instagramUrl = (footerData && footerData.instagramUrl) ? footerData.instagramUrl : "https://www.instagram.com/strategicgreenai/";

  return (
    <footer style={{ background: "#04100D", color: "#94a3b8", padding: "64px 24px 32px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="footer-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1.4fr", gap: 48, marginBottom: 48, alignItems: "start" }}>

          {/* Logo + SBI venture + socials */}
          <div className="footer-logo-col">
            <div style={{ filter: "brightness(0) invert(1)" }}>
              <Logo height={24} />
            </div>
            {/* SBI venture pill */}
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "#475569" }}>A venture of</span>
              <a href="https://sbi.international/" target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 10px", borderRadius: 7,
                  border: "1px solid #1e2d42", textDecoration: "none", background: "rgba(255,255,255,0.03)",
                  transition: "border-color 0.2s, background 0.2s" }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = "#334155"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = "#1e2d42"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}>
                <img src="/sbi-logo-white.svg" alt="Strategic Building Innovation" height="28" style={{ display: "block" }} />
              </a>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <a href={linkedinUrl} target="_blank" rel="noreferrer"
                style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid #1e2d42", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0, transition: "border-color 0.2s" }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = "#0A66C2"; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = "#1e2d42"; }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="2" y="9" width="4" height="12" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="4" cy="4" r="2" stroke="#64748b" strokeWidth="2"/>
                </svg>
              </a>
              <a href={instagramUrl} target="_blank" rel="noreferrer"
                style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid #1e2d42", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0, transition: "border-color 0.2s" }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = "#E1306C"; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = "#1e2d42"; }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Nav columns */}
          {[
            { title: "Solutions", links: [["StrategicGreen.ai","Solutions"],["StrategicGreen.ai LITE","Solutions"],["BIM / Drawing AI","Solutions"],["Pricing","Pricing"]] },
            { title: "Company",   links: [["About Us","About"],["FAQ","FAQ"],["Blog","Blog"]] },
            { title: "Legal",     links: [["Privacy Policy","Privacy"],["Terms of Service","Terms"]] }
          ].map(function(col) {
            return (
              <div key={col.title}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>{col.title}</div>
                {col.links.map(function(l) {
                  return (
                    <button key={l[0]} onClick={function() { setPage(l[1]); }}
                      style={{ display: "block", fontSize: 14, color: "#64748b", background: "none", border: "none", cursor: "pointer", marginBottom: 10, padding: 0, fontFamily: "Google Sans, sans-serif", textAlign: "left" }}
                      onMouseEnter={function(e) { e.currentTarget.style.color = "#94a3b8"; }}
                      onMouseLeave={function(e) { e.currentTarget.style.color = "#64748b"; }}>
                      {l[0]}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Contact column */}
          <div className="footer-contact-col">
            <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>Contact</div>
            <ContactPopup contactEmail={contactEmail} />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom" style={{ borderTop: "1px solid #1e2d42", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, fontSize: 13, color: "#475569" }}>
          <span>© 2026 Strategic Green Innovation — All rights reserved.</span>
          <span style={{ display: "flex", gap: 16 }}>
            <button onClick={function() { setPage("Privacy"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#475569", fontFamily: "Google Sans, sans-serif", padding: 0 }}
              onMouseEnter={function(e) { e.currentTarget.style.color = "#94a3b8"; }}
              onMouseLeave={function(e) { e.currentTarget.style.color = "#475569"; }}>Privacy</button>
            <span style={{ color: "#2a3a4a" }}>·</span>
            <button onClick={function() { setPage("Terms"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#475569", fontFamily: "Google Sans, sans-serif", padding: 0 }}
              onMouseEnter={function(e) { e.currentTarget.style.color = "#94a3b8"; }}
              onMouseLeave={function(e) { e.currentTarget.style.color = "#475569"; }}>Terms &amp; Conditions</button>
          </span>
        </div>
      </div>
    </footer>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// PRODUCT DEMO FLOW COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function ProductDemoFlow() {
  var stepState = useState(1);
  var step = stepState[0];
  var setStep = stepState[1];
  var autoRef = { current: null };
  var TOTAL = 5;
  var BH = [42,21,62,68,74,90,82,76,55,46,38,30];

  var CHAT_PATH = "M6.14148 0.204407C7.66144 -0.159701 9.25527 -0.0711756 10.7255 0.45929C12.1955 0.989773 13.4786 1.93911 14.4159 3.18976C15.3531 4.44042 15.9035 5.93849 15.9999 7.49835V12.6663C15.999 13.5501 15.6473 14.3978 15.0223 15.0228C14.3975 15.6475 13.5505 15.9994 12.6669 16.0003H8.0116C6.44881 16.0011 4.91966 15.5443 3.61316 14.6868C2.30656 13.8291 1.27896 12.6075 0.658081 11.1732C0.0372207 9.73884 -0.149827 8.15419 0.119019 6.61456C0.387884 5.0749 1.10149 3.64715 2.17175 2.50812C3.24191 1.36934 4.62178 0.568504 6.14148 0.204407ZM8.01355 1.33331C7.72397 1.33371 7.43467 1.35199 7.14734 1.388C5.6736 1.58274 4.30641 2.26221 3.2616 3.31964C2.21676 4.37715 1.55379 5.75278 1.37683 7.22882C1.26479 8.16457 1.35387 9.11362 1.63855 10.012C1.92322 10.9104 2.39678 11.7374 3.02722 12.4378C3.65474 13.1386 4.42334 13.6993 5.2821 14.0833C6.14082 14.4673 7.07094 14.6659 8.0116 14.6663H12.6669C13.1972 14.6663 13.7059 14.4554 14.0809 14.0804C14.4558 13.7053 14.6669 13.1966 14.6669 12.6663V7.56085C14.6054 6.65683 14.3605 5.77488 13.9481 4.96808C13.5356 4.16109 12.9635 3.44581 12.2665 2.86652C11.073 1.87188 9.56712 1.32908 8.01355 1.33331ZM10.6669 10.0003C10.8436 10.0004 11.0136 10.0706 11.1385 10.1956C11.2632 10.3205 11.3328 10.4898 11.3329 10.6663C11.3329 10.843 11.2634 11.013 11.1385 11.138C11.0136 11.263 10.8436 11.3333 10.6669 11.3333H5.33289C5.15629 11.3332 4.98709 11.2629 4.86218 11.138C4.73716 11.013 4.66687 10.8431 4.66687 10.6663C4.66696 10.4897 4.73728 10.3206 4.86218 10.1956C4.9871 10.0707 5.15624 10.0004 5.33289 10.0003H10.6669ZM10.6669 7.33331C10.8436 7.33336 11.0136 7.40365 11.1385 7.52863C11.2634 7.65362 11.3329 7.82362 11.3329 8.00031C11.3328 8.1768 11.2632 8.34611 11.1385 8.47101C11.0136 8.59599 10.8436 8.66627 10.6669 8.66632H5.33289C5.15624 8.6662 4.9871 8.59592 4.86218 8.47101C4.73728 8.34607 4.66696 8.17696 4.66687 8.00031C4.66687 7.82349 4.73716 7.65365 4.86218 7.52863C4.98709 7.40376 5.15628 7.33344 5.33289 7.33331H10.6669ZM7.99988 4.66632C8.17665 4.66632 8.34654 4.73666 8.47156 4.86163C8.59658 4.98665 8.66687 5.15651 8.66687 5.33331C8.66687 5.51012 8.59658 5.67997 8.47156 5.80499C8.34654 5.92998 8.17666 6.00031 7.99988 6.00031H5.33289C5.15636 6.00018 4.98707 5.92975 4.86218 5.80499C4.73716 5.67997 4.66687 5.51012 4.66687 5.33331C4.66687 5.15651 4.73716 4.98665 4.86218 4.86163C4.98707 4.73688 5.15637 4.66644 5.33289 4.66632H7.99988Z";

  var FILES = [
    { name: "electricity_mar_2025.pdf", size: "284 KB", bg: "#FEF9EC", tc: "#92400E", ext: "PDF" },
    { name: "water_jun_2025.pdf",        size: "196 KB", bg: "#EFF6FF", tc: "#1E40AF", ext: "PDF" },
    { name: "gas_sep_2025.png",          size: "512 KB", bg: "#F0FDF4", tc: "#166534", ext: "PNG" }
  ];

  var SECTIONS = [
    { label: "Purchase of Electricity", dot: "#22A7FF",
      cols: ["#","File","Qty","Unit","From","To","Meter no.","Amount","CCY"],
      widths: [16,68,26,24,60,60,54,34,26],
      row: ["1","PA#101_elec.pdf","1,202","kWh","29 Jan 2026","24 Feb 2026","15623123","23,129","HKD"] },
    { label: "Water Consumption", dot: "#3490FD",
      cols: ["#","File","Qty","Unit","From","To","Meter no.","Amount","CCY"],
      widths: [16,68,26,30,60,60,64,34,26],
      row: ["1","PA#102_water.pdf","923","gallon","28 Dec 2025","14 Feb 2026","ME31291242","12,092","HKD"] },
    { label: "Gas Consumption", dot: "#FF8D4E",
      cols: ["#","File","Qty","Unit","From","To","Meter no.","Amount","CCY"],
      widths: [16,68,26,22,60,60,54,34,26],
      row: ["1","PA#103_gas.pdf","4,410","MJ","1 Mar 2025","31 Mar 2025","GS99120381","6,840","HKD"] }
  ];

  var CHAT = [
    { role: "ai",   text: "Your building used 56,678 kWh this year. I've spotted 3 energy reduction opportunities that could cut Scope 2 emissions by up to 22%." },
    { role: "user", text: "What's the top recommendation?" },
    { role: "ai",   hi: true, text: "Switch HVAC to off-peak scheduling. July & August are your peak months — shifting cooling loads by 2 hours could save ~10,200 kWh/year and reduce GHG by 0.06 tCO\u2082e/ft\u00b2." },
    { role: "user", text: "Any low-cost quick wins?" },
    { role: "ai",   text: "Yes — LED retrofit in common areas and stairwells. Estimated 8\u201312% electricity saving with payback under 18 months. No major capex required." }
  ];

  var HINTS = [
    "Click AI-track emission to begin",
    "Upload your utility bills",
    "Review AI-extracted data",
    "Dashboard populating with your data",
    "AI Chat: energy reduction recommendations"
  ];

  // bar heights state
  var barState = useState(BH.map(function() { return 0; }));
  var barHeights = barState[0];
  var setBarHeights = barState[1];

  // card values state
  var ghgState = useState("—"); var ghgVal = ghgState[0]; var setGhgVal = ghgState[1];
  var kwhState = useState("—"); var kwhVal = kwhState[0]; var setKwhVal = kwhState[1];
  var chartState = useState("—"); var chartVal = chartState[0]; var setChartVal = chartState[1];
  var periodState = useState("No data yet"); var periodVal = periodState[0]; var setPeriodVal = periodState[1];
  var periodColorState = useState("#d1d5db"); var periodColor = periodColorState[0]; var setPeriodColor = periodColorState[1];

  // upload file visibility
  var filesState = useState([]); var visibleFiles = filesState[0]; var setVisibleFiles = filesState[1];
  var extractBtnState = useState(false); var showExtract = extractBtnState[0]; var setShowExtract = extractBtnState[1];
  var dzActiveState = useState(false); var dzActive = dzActiveState[0]; var setDzActive = dzActiveState[1];

  // review cells
  var reviewCellsState = useState({}); var reviewCells = reviewCellsState[0]; var setReviewCells = reviewCellsState[1];
  var reviewRowsState = useState(false); var reviewRowsVisible = reviewRowsState[0]; var setReviewRowsVisible = reviewRowsState[1];
  var confirmBtnState = useState(false); var showConfirm = confirmBtnState[0]; var setShowConfirm = confirmBtnState[1];

  // chat messages
  var chatMsgsState = useState([]); var chatMsgs = chatMsgsState[0]; var setChatMsgs = chatMsgsState[1];

  function clr() {
    if (autoRef.current) { clearTimeout(autoRef.current); autoRef.current = null; }
  }

  function resetDash() {
    setGhgVal("—"); setKwhVal("—"); setChartVal("—");
    setPeriodVal("No data yet"); setPeriodColor("#d1d5db");
    setBarHeights(BH.map(function() { return 0; }));
  }

  useEffect(function() {
    var btn = document.getElementById("demo-ai-btn");
    if (btn) {
      btn.setAttribute("style", "display:inline-block!important;height:34px!important;padding:0 16px!important;background-color:#04100D!important;background-image:none!important;border:none!important;border-radius:8px!important;cursor:pointer!important;margin-left:auto!important;flex-shrink:0!important;font-family:Urbanist,sans-serif!important;font-size:13px!important;font-weight:600!important;color:#fff!important;-webkit-text-fill-color:#fff!important;line-height:34px!important;white-space:nowrap!important;");
    }
  }, [step]);

  useEffect(function() {
    clr();
    if (step === 1) {
      resetDash();
      autoRef.current = setTimeout(function() { setStep(2); }, 5000);
    } else if (step === 2) {
      setVisibleFiles([]); setShowExtract(false); setDzActive(false);
      setTimeout(function() { setDzActive(true); }, 400);
      setTimeout(function() { setDzActive(false); }, 1100);
      FILES.forEach(function(f, i) {
        setTimeout(function() {
          setVisibleFiles(function(prev) { return prev.concat([i]); });
          if (i === FILES.length - 1) {
            setTimeout(function() { setShowExtract(true); }, 350);
          }
        }, 900 + i * 420);
      });
      autoRef.current = setTimeout(function() { setStep(3); }, 5800);
    } else if (step === 3) {
      setReviewCells({}); setReviewRowsVisible(false); setShowConfirm(false);
      setTimeout(function() { setReviewRowsVisible(true); }, 200);
      var delay = 300;
      SECTIONS.forEach(function(sec, si) {
        sec.cols.forEach(function(col, ci) {
          (function(s, c, v, d) {
            setTimeout(function() {
              setReviewCells(function(prev) {
                var next = Object.assign({}, prev);
                next[s + "_" + c] = v;
                return next;
              });
            }, d);
          })(si, ci, sec.row[ci], delay);
          delay += 40;
        });
      });
      var totalDelay = delay + 250;
      setTimeout(function() { setShowConfirm(true); }, totalDelay);
      autoRef.current = setTimeout(function() { setStep(4); }, totalDelay + 2000);
    } else if (step === 4) {
      resetDash();
      setTimeout(function() { setGhgVal("0.31"); }, 300);
      setTimeout(function() { setKwhVal("56,678"); }, 550);
      setTimeout(function() { setChartVal("56,678"); setPeriodVal("Mar 2025 \u2013 Feb 2026"); setPeriodColor("#646C6A"); }, 800);
      BH.forEach(function(h, i) {
        setTimeout(function() {
          setBarHeights(function(prev) {
            var next = prev.slice();
            next[i] = h;
            return next;
          });
        }, 500 + i * 80);
      });
      autoRef.current = setTimeout(function() { setStep(5); }, 5000);
    } else if (step === 5) {
      setChatMsgs([]);
      var msgs = [];
      var delay2 = 500;
      CHAT.forEach(function(msg, i) {
        if (msg.role === "ai") {
          (function(idx, m, d) {
            setTimeout(function() {
              setChatMsgs(function(prev) { return prev.concat([{ role: "ai", text: "", hi: m.hi, typing: true, id: idx }]); });
              setTimeout(function() {
                setChatMsgs(function(prev) {
                  return prev.map(function(p) {
                    return p.id === idx ? Object.assign({}, p, { typing: false, text: m.text }) : p;
                  });
                });
              }, 700);
            }, d);
          })(i, msg, delay2);
          delay2 += 700 + msg.text.length * 16 + 450;
        } else {
          (function(idx, m, d) {
            setTimeout(function() {
              setChatMsgs(function(prev) { return prev.concat([{ role: "user", text: m.text, id: idx }]); });
            }, d);
          })(i, msg, delay2);
          delay2 += 500;
        }
      });
      autoRef.current = setTimeout(function() { setStep(1); }, delay2 + 3000);
    }
    return function() { clr(); };
  }, [step]);

  var gradId = "chatGradDemo";
  var clipId = "chatClipDemo";

  var ChatIcon = function(props) {
    var sz = props.size || 16;
    return (
      <svg width={sz} height={sz} viewBox="0 0 16 16" fill="none">
        <g clipPath={"url(#" + clipId + ")"}>
          <path d={CHAT_PATH} fill={"url(#" + gradId + ")"} />
        </g>
      </svg>
    );
  };

  var sbBase = { display: "flex", alignItems: "center", gap: 7, padding: "7px 8px", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#04100D", cursor: "pointer" };
  var showUpload = step === 2;
  var showReview = step === 3;
  var showChat = step === 5;
  var aiSidebarOn = step === 5;

  return (
    <section style={{ padding: "0 24px 80px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        {/* heading */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>The Platform</div>
          <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px", color: "#04100D", marginBottom: 10 }}>
            From bill to dashboard in seconds
          </h2>
          <p style={{ fontSize: 14, color: "#646C6A", maxWidth: 400, margin: "0 auto", lineHeight: 1.75 }}>
            Upload any utility bill and AI extracts and populates your GHG dashboard automatically.
          </p>
        </div>

        {/* SVG defs */}
        <svg width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <linearGradient id={gradId} x1="14.5332" y1="2.25268" x2="-0.011" y2="13.184" gradientUnits="userSpaceOnUse">
              <stop stopColor="#22A7FF" />
              <stop offset="0.35" stopColor="#165895" />
              <stop offset="0.62" stopColor="#D52D9B" />
              <stop offset="1" stopColor="#F79964" />
            </linearGradient>
            <clipPath id={clipId}><rect width="16" height="16" fill="white" /></clipPath>
          </defs>
        </svg>

        {/* App frame */}
        <style>{"\n          .demo-app { display: grid; grid-template-columns: 155px 1fr; }\n          .demo-sb { width: 155px; min-width: 155px; }\n          .demo-sb-label { display: block; }\n          .demo-sb-text { display: inline; }\n          @media (max-width: 520px) {\n            .demo-app { grid-template-columns: 44px 1fr; }\n            .demo-sb { width: 44px; min-width: 44px; }\n            .demo-sb-logo { font-size: 13px !important; padding: 10px 6px !important; }\n            .demo-sb-label { display: none; }\n            .demo-sb-text { display: none; }\n            .demo-sb-item { justify-content: center; padding: 7px 0 !important; gap: 0 !important; }\n            .demo-sb-footer { padding: 8px 0 !important; justify-content: center; }\n            .demo-sb-footer-name { display: none; }\n          }\n        "}</style>
        <div className="demo-app" style={{ position: "relative", background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #E8E8E8", boxShadow: "0 16px 48px rgba(0,0,0,.08)", minHeight: 420 }}>

          {/* SIDEBAR */}
          <div className="demo-sb" style={{ background: "#fff", borderRight: "1px solid #E8E8E8", display: "flex", flexDirection: "column" }}>
            <div className="demo-sb-logo" style={{ padding: "14px 14px 10px", borderBottom: "1px solid #E8E8E8", fontSize: 18, fontWeight: 800, color: "#04100D" }}>sgi</div>
            <div style={{ padding: 8, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              <div className="demo-sb-label" style={{ fontSize: 10, fontWeight: 600, color: "#646C6A", padding: "5px 6px 3px", letterSpacing: ".04em" }}>Bookmarked</div>
              <div className="demo-sb-item" style={Object.assign({}, sbBase, { background: step !== 5 ? "rgba(4,16,13,0.06)" : "transparent" })}>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, background: "#e8ecf0" }} />
                <span className="demo-sb-text">Palo Alto Bldg</span>
              </div>
              <div className="demo-sb-item" style={sbBase}>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, background: "#e8ecf0" }} />
                <span className="demo-sb-text">San Jose Tower</span>
              </div>
              <div className="demo-sb-item" style={sbBase}>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, background: "#e8ecf0" }} />
                <span className="demo-sb-text">SF Centre</span>
              </div>
              <div style={{ height: 1, background: "#E8E8E8", margin: "4px 0" }} />
              <div className="demo-sb-item" onClick={function() { clr(); setStep(5); }}
                style={Object.assign({}, sbBase, aiSidebarOn ? { background: "rgba(34,167,255,0.08)", color: "#22A7FF" } : {})}>
                <div style={{ width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: aiSidebarOn ? 1 : 0.55 }}>
                  <ChatIcon size={16} />
                </div>
                <span className="demo-sb-text">AI Chat</span>
              </div>
            </div>
            <div className="demo-sb-footer" style={{ padding: "10px 12px", borderTop: "1px solid #E8E8E8", fontSize: 11, fontWeight: 600, color: "#04100D", display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 24, height: 24, borderRadius: 7, background: "#f0f4f8", flexShrink: 0 }} />
              <span className="demo-sb-footer-name">Grace</span>
            </div>
          </div>

          {/* MAIN AREA */}
          <div style={{ display: "flex", flexDirection: "column", background: "rgba(4,16,13,0.03)", position: "relative" }}>

            {/* Header */}
            <div style={{ background: "#fff", padding: "12px 16px 0", borderBottom: "1px solid #E8E8E8" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#04100D", marginBottom: 2 }}>Palo Alto International Building</div>
              <div style={{ fontSize: 10, color: "#646C6A", marginBottom: 9 }}>California, USA · 212,923 ft² · Office</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
                <div style={{ padding: "7px 10px", fontSize: 11, fontWeight: 600, color: "#04100D", position: "relative" }}>
                  Overview
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "#25A385", borderRadius: 2 }} />
                </div>
                <div style={{ width: 64, height: 11, borderRadius: 3, background: "#f1f3f2", alignSelf: "center", marginBottom: 4 }} />
                <div style={{ width: 52, height: 11, borderRadius: 3, background: "#f1f3f2", alignSelf: "center", marginBottom: 4 }} />
                <div style={{ width: 70, height: 11, borderRadius: 3, background: "#f1f3f2", alignSelf: "center", marginBottom: 4 }} />
              </div>
            </div>

            {/* Toolbar */}
            <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 6, background: "#fff", borderBottom: "1px solid #E8E8E8" }}>
              <div style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid #9BA3A1", background: "#fff", fontSize: 11, color: "#04100D" }}>1 Mar 2025 — 28 Feb 2026</div>
              <button id="demo-ai-btn" onClick={function() { clr(); setStep(2); }}>AI-track emission</button>
            </div>

            {/* Dashboard */}
            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#646C6A", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 7 }}>Total GHG</div>
                  <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-1px", lineHeight: 1, color: ghgVal === "—" ? "#d1d5db" : "#04100D", transition: "color .4s" }}>{ghgVal}</div>
                  <div style={{ fontSize: 10, color: "#646C6A", marginTop: 3 }}>tCO₂e / ft²</div>
                </div>
                <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#646C6A", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 7 }}>Electricity</div>
                  <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-1px", lineHeight: 1, color: kwhVal === "—" ? "#d1d5db" : "#04100D", transition: "color .4s" }}>{kwhVal}</div>
                  <div style={{ fontSize: 10, color: "#646C6A", marginTop: 3 }}>kWh</div>
                </div>
              </div>
              <div style={{ background: "#fff", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#04100D" }}>Electricity Consumption</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 3 }}>
                      <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-1px", color: chartVal === "—" ? "#d1d5db" : "#04100D", transition: "color .4s" }}>{chartVal}</span>
                      <span style={{ fontSize: 10, color: "#646C6A" }}>kWh total</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: periodColor, transition: "color .4s" }}>{periodVal}</div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 68 }}>
                  {BH.map(function(h, i) {
                    return (
                      <div key={i} style={{ flex: 1, borderRadius: "3px 3px 0 0", background: "#FF8D4E", height: barHeights[i] + "%", opacity: barHeights[i] === 0 ? 0.2 : 1, transition: "height .6s ease, opacity .4s" }} />
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                  {["Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb"].map(function(m) {
                    return <div key={m} style={{ flex: 1, fontSize: 8, textAlign: "center", color: "#646C6A" }}>{m}</div>;
                  })}
                </div>
              </div>
            </div>

            {/* STEP 3: REVIEW */}
            {showReview && (
              <div style={{ position: "absolute", inset: 0, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 10 }}>
                <div style={{ padding: "10px 14px 8px", background: "#fff", borderBottom: "1px solid #E8E8E8", flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#04100D" }}>Review extracted data</div>
                  <div style={{ fontSize: 10, color: "#646C6A", marginTop: 2 }}>Data extraction complete — please review before confirming.</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 0 8px" }}>
                  {SECTIONS.map(function(sec, si) {
                    return (
                      <div key={si} style={{ padding: si === 0 ? "8px 14px 0" : "16px 14px 0", opacity: reviewRowsVisible ? 1 : 0, transition: "opacity .25s " + (si * 80) + "ms" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#04100D", display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: sec.dot, flexShrink: 0 }} />
                          {sec.label}
                        </div>
                        <div style={{ overflowX: "auto", paddingBottom: 6 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                            <thead>
                              <tr style={{ borderTop: "1px solid #9BA3A1", borderBottom: "1px solid #9BA3A1" }}>
                                {sec.cols.map(function(c) {
                                  return <th key={c} style={{ padding: "5px 7px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "#646C6A", whiteSpace: "nowrap", background: "#fff", textTransform: "uppercase", letterSpacing: ".03em" }}>{c}</th>;
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid #F0F0F0", opacity: reviewRowsVisible ? 1 : 0, transition: "opacity .2s " + (150 + si * 100) + "ms" }}>
                                {sec.cols.map(function(c, ci) {
                                  var val = reviewCells[si + "_" + ci];
                                  return (
                                    <td key={ci} style={{ padding: "5px 7px", fontSize: 10, fontWeight: 500, color: "#04100D", whiteSpace: "nowrap", minWidth: 32 }}>
                                      {val !== undefined ? (
                                        <span style={{ opacity: 1, transition: "opacity .15s" }}>{val}</span>
                                      ) : (
                                        <span style={{ display: "inline-block", height: 9, borderRadius: 3, background: "linear-gradient(90deg,#f0f0f0 25%,#e4e4e4 50%,#f0f0f0 75%)", backgroundSize: "400px 100%", width: sec.widths[ci] || 40, animation: "shimmer 1.1s infinite linear" }} />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: "8px 14px", borderTop: "1px solid #E8E8E8", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", background: "#fff", minHeight: 46 }}>
                  <button onClick={function() { clr(); setStep(4); }}
                    style={{ height: 30, padding: "0 16px", background: "#04100D", border: "none", borderRadius: 7, color: "#fff", fontFamily: "Urbanist,sans-serif", fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: showConfirm ? 1 : 0, transition: "opacity .4s", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    Confirm &amp; save →
                  </button>
                </div>
              </div>
            )}

            {/* STEP 5: CHAT */}
            {showChat && (
              <div style={{ position: "absolute", inset: 0, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 10 }}>
                <div style={{ padding: "10px 14px 9px", borderBottom: "1px solid #E8E8E8", flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 7, background: "#fff", border: "1px solid #E8E8E8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <ChatIcon size={16} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#04100D" }}>strategicgreen AI</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8, background: "#FAFBFA" }}>
                  {chatMsgs.map(function(msg) {
                    if (msg.role === "ai") {
                      return (
                        <div key={msg.id} style={{ display: "flex", gap: 7, alignItems: "flex-start", opacity: 1, transform: "translateY(0)", transition: "opacity .3s, transform .3s" }}>
                          <div style={{ width: 22, height: 22, borderRadius: 6, background: "#fff", border: "1px solid #E8E8E8", flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <ChatIcon size={13} />
                          </div>
                          <div style={{ background: msg.hi ? "#EFF9FF" : "#fff", borderRadius: "0 8px 8px 8px", padding: "8px 10px", fontSize: 10.5, color: "#04100D", lineHeight: 1.6, maxWidth: "88%", border: msg.hi ? "1px solid #BDE8FF" : "1px solid #E8E8E8" }}>
                            {msg.typing ? (
                              <span style={{ display: "flex", gap: 3, alignItems: "center" }}>
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#9BA3A1", display: "inline-block" }} />
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#9BA3A1", display: "inline-block" }} />
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#9BA3A1", display: "inline-block" }} />
                              </span>
                            ) : msg.text}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div key={msg.id} style={{ display: "flex", justifyContent: "flex-end" }}>
                          <div style={{ background: "#04100D", borderRadius: "8px 0 8px 8px", padding: "8px 10px", fontSize: 10.5, color: "#fff", lineHeight: 1.6, maxWidth: "80%" }}>{msg.text}</div>
                        </div>
                      );
                    }
                  })}
                </div>
                <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #E8E8E8", flexShrink: 0, display: "flex", alignItems: "center", gap: 8, background: "#fff" }}>
                  <div style={{ flex: 1, height: 32, border: "1px solid #E8E8E8", borderRadius: 8, padding: "0 10px", fontSize: 10.5, color: "#9BA3A1", background: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
                    <ChatIcon size={11} />
                    <span>Ask anything about your assets / portfolio</span>
                  </div>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: "#04100D", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M2 8h12M9 3l5 5-5 5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* STEP 2: UPLOAD — full-app overlay */}
          {showUpload && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(4,16,13,0.18)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20 }}>
              <div style={{ width: 300, background: "#fff", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,.18)", overflow: "hidden" }}>
                <div style={{ padding: "14px 14px 9px", borderBottom: "1px solid #E8E8E8" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#04100D" }}>Upload utility bills</div>
                  <div style={{ fontSize: 10, color: "#646C6A", marginTop: 2, lineHeight: 1.5 }}>AI will extract data automatically.</div>
                </div>
                <div style={{ margin: 10, border: dzActive ? "1.5px dashed #22A7FF" : "1.5px dashed #D0D5D3", borderRadius: 8, background: dzActive ? "#F0F8FF" : "#FAFBFA", padding: "12px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, transition: "border-color .2s, background .2s" }}>
                  <div style={{ width: 28, height: 28, background: "#F0F8FF", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 4v12M7 9l5-5 5 5" stroke="#22A7FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="#22A7FF" strokeWidth="2" strokeLinecap="round" /></svg>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#04100D" }}>Drop files or click to browse</div>
                  <div style={{ fontSize: 10, color: "#646C6A" }}>Any utility bill format</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                    {["PDF","PNG","JPG","XLSX"].map(function(t) {
                      return <span key={t} style={{ padding: "1px 6px", border: "1px solid #E8E8E8", borderRadius: 100, fontSize: 9, fontWeight: 600, color: "#9BA3A1", background: "#fff" }}>{t}</span>;
                    })}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "0 10px" }}>
                  {FILES.map(function(f, i) {
                    var visible = visibleFiles.indexOf(i) !== -1;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 9px", background: "#fff", border: "1px solid #E8E8E8", borderRadius: 7, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(4px)", transition: "opacity .3s, transform .3s" }}>
                        <div style={{ width: 22, height: 22, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800, flexShrink: 0, background: f.bg, color: f.tc }}>{f.ext}</div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#04100D", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                        <span style={{ fontSize: 9, color: "#9BA3A1", flexShrink: 0 }}>{f.size}</span>
                        <div style={{ width: 13, height: 13, borderRadius: "50%", background: "#25A385", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <svg width="7" height="7" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: "9px 10px 12px" }}>
                  <button onClick={function() { clr(); setStep(3); }}
                    style={{ width: "100%", height: 32, background: "#04100D", border: "none", borderRadius: 8, color: "#fff", fontFamily: "Urbanist,sans-serif", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, opacity: showExtract ? 1 : 0, transition: "opacity .3s" }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 4v12M7 9l5-5 5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg>
                    Upload &amp; extract with AI
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Progress nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 14 }}>
          <button onClick={function() { if (step > 1) { clr(); setStep(step - 1); } }}
            style={{ padding: "7px 16px", borderRadius: 7, border: "1px solid #9BA3A1", background: "#fff", color: "#04100D", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Urbanist,sans-serif", flexShrink: 0, opacity: step === 1 ? 0.3 : 1 }}>
            ← Back
          </button>
          <div style={{ display: "flex", gap: 5, flex: 1 }}>
            {[1,2,3,4,5].map(function(s) {
              return (
                <div key={s} onClick={function() { clr(); setStep(s); }}
                  style={{ flex: 1, height: 6, borderRadius: 3, background: "#E8E8E8", overflow: "hidden", position: "relative", cursor: "pointer" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: s <= step ? "100%" : "0%", background: "#22A7FF", borderRadius: 3, transition: "width .4s cubic-bezier(.4,0,.2,1)" }} />
                </div>
              );
            })}
          </div>
          <button onClick={function() { clr(); setStep(step >= TOTAL ? 1 : step + 1); }}
            style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: "#04100D", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Urbanist,sans-serif", flexShrink: 0 }}>
            {step >= TOTAL ? "Replay ↺" : "Next →"}
          </button>
        </div>
        <div style={{ textAlign: "center", fontSize: 11, color: "#646C6A", marginTop: 6 }}>{HINTS[step - 1]}</div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VIDEO SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function VideoSection() {
  var YT_ID = "6MbloO9UoiI";
  var ytUrl = "https://www.youtube.com/watch?v=" + YT_ID;
  var thumbState = useState(false);
  var hovered = thumbState[0];
  var setHovered = thumbState[1];

  return (
    <section style={{ padding: "0 24px 100px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Label + heading */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div className="section-label" style={{ marginBottom: 14 }}>See It In Action</div>
          <h2 style={{ fontSize: "clamp(24px,3.5vw,42px)", fontWeight: 800, letterSpacing: "-1.5px", color: "#04100D", marginBottom: 14 }}>
            Watch how it works
          </h2>
          <p style={{ fontSize: 16, color: "#64748b", maxWidth: 480, margin: "0 auto", lineHeight: 1.75 }}>
            See how StrategicGreen.ai transforms messy utility bills into clean, structured data in seconds.
          </p>
        </div>

        {/* Thumbnail card */}
        <a href={ytUrl} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}
          onMouseEnter={function() { setHovered(true); }}
          onMouseLeave={function() { setHovered(false); }}>
          <div style={{ padding: 3, borderRadius: 22, background: "linear-gradient(135deg, #22A7FF, #165895, #D52D9B, #F79964)", boxShadow: hovered ? "0 32px 100px rgba(34,167,255,0.22)" : "0 24px 80px rgba(34,167,255,0.15)", transform: hovered ? "translateY(-3px)" : "translateY(0)", transition: "all 0.2s ease" }}>
            <div style={{ borderRadius: 19, overflow: "hidden", position: "relative", paddingBottom: "56.25%" }}>
              <div style={{ position: "absolute", inset: 0 }}>

                {/* YouTube thumbnail — loaded from YouTube CDN */}
                <img src="https://img.youtube.com/vi/6MbloO9UoiI/maxresdefault.jpg" alt="StrategicGreen.ai Product Overview" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}/>

                {/* Hover overlay */}
                <div style={{ position: "absolute", inset: 0, background: hovered ? "rgba(4,16,13,0.45)" : "rgba(4,16,13,0.15)", transition: "background 0.2s ease" }}/>

                {/* Play button — always visible, pops on hover */}
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
                  <div style={{ width: hovered ? 88 : 76, height: hovered ? 88 : 76, borderRadius: "50%", background: "#fff", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease" }}>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <defs>
                        <linearGradient id="pg2" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#22A7FF"/>
                          <stop offset="55%" stopColor="#165895"/>
                          <stop offset="100%" stopColor="#D52D9B"/>
                        </linearGradient>
                      </defs>
                      <path d="M9 5.5L22 14L9 22.5V5.5Z" fill="url(#pg2)"/>
                    </svg>
                  </div>
                </div>

                {/* Bottom bar */}
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 60, background: "linear-gradient(to top, rgba(4,16,13,0.85), transparent)", display: "flex", alignItems: "flex-end", padding: "0 24px 14px", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>StrategicGreen.ai — Product Overview</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1.5C3.96 1.5 1.5 3.96 1.5 7S3.96 12.5 7 12.5 12.5 10.04 12.5 7 10.04 1.5 7 1.5zm-1 8V4.5l4 2.5-4 2.5z" fill="rgba(255,255,255,0.55)"/>
                    </svg>
                    Watch on YouTube
                  </div>
                </div>

              </div>
            </div>
          </div>
        </a>

        {/* Caption */}
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Click to watch · Opens on YouTube</div>
          <a href={ytUrl} target="_blank" rel="noreferrer"
            style={{ fontSize: 13, fontWeight: 600, color: "#165895", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M8 3l4 4-4 4" stroke="#165895" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Watch on YouTube
          </a>
        </div>

      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOME PAGE
// ═══════════════════════════════════════════════════════════════════════════
function HomePage(props) {
  var setPage = props.setPage;

  // ── Sanity: site settings ──
  var defaultSettings = {
    heroHeadline: "Agentic AI for a Sustainable Built Environment.",
    heroSubheadline: "No templates, no mapping. Automatically extract, calculate, and benchmark — from utility bills to BIM models.",
    heroStats: []
  };
  var settingsResult = useSanity('*[_type == "siteSettings"][0]{heroHeadline,heroSubheadline,heroStats}', defaultSettings);
  var settings = settingsResult[0];

  var fallbackStats = [
    { num: "2,000+", label: "Bills tested" },
    { num: "8",      label: "Languages supported" },
    { num: "10\u00d7",    label: "ROI" },
    { num: "94%",    label: "Faster with AI" },
    { num: "100%",   label: "Accuracy (up to)" }
  ];

  // ── Cursor glow tracker ──
  useEffect(function() {
    var glow = document.getElementById("cursor-glow");
    if (!glow) return;
    var raf = null;
    var tx = window.innerWidth / 2;
    var ty = window.innerHeight / 2;
    var cx = tx; var cy = ty;

    function onMove(e) { tx = e.clientX; ty = e.clientY; }
    function tick() {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      glow.style.transform = "translate(" + (cx - 300) + "px, " + (cy - 300) + "px)";
      raf = requestAnimationFrame(tick);
    }
    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return function() {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // ── Magnetic buttons ──
  useEffect(function() {
    var btns = document.querySelectorAll(".mag-btn");
    var cleanups = [];
    btns.forEach(function(btn) {
      function onEnter() { btn.style.transition = "transform 0.1s ease"; }
      function onLeave() {
        btn.style.transition = "transform 0.4s cubic-bezier(0.34,1.56,0.64,1)";
        btn.style.transform = "translate(0px, 0px)";
      }
      function onMove(e) {
        var r = btn.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        btn.style.transform = "translate(" + dx * 0.28 + "px, " + dy * 0.28 + "px)";
      }
      btn.addEventListener("mouseenter", onEnter);
      btn.addEventListener("mouseleave", onLeave);
      btn.addEventListener("mousemove", onMove);
      cleanups.push(function() {
        btn.removeEventListener("mouseenter", onEnter);
        btn.removeEventListener("mouseleave", onLeave);
        btn.removeEventListener("mousemove", onMove);
      });
    });
    return function() { cleanups.forEach(function(fn) { fn(); }); };
  }, []);

  // ── Card 3D tilt ──
  useEffect(function() {
    var cards = document.querySelectorAll(".tilt-card");
    var cleanups = [];
    cards.forEach(function(card) {
      function onMove(e) {
        var r = card.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width  - 0.5;
        var y = (e.clientY - r.top)  / r.height - 0.5;
        card.style.transform = "perspective(600px) rotateY(" + (x * 10) + "deg) rotateX(" + (-y * 10) + "deg) translateZ(6px)";
        card.style.boxShadow = "0 " + (12 + y * 8) + "px " + (40 + Math.abs(x) * 20) + "px rgba(34,167,255,0.13)";
      }
      function onLeave() {
        card.style.transform = "perspective(600px) rotateY(0deg) rotateX(0deg) translateZ(0px)";
        card.style.boxShadow = "";
        card.style.transition = "transform 0.5s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.5s ease";
      }
      function onEnter() { card.style.transition = "transform 0.1s ease, box-shadow 0.1s ease"; }
      card.addEventListener("mousemove", onMove);
      card.addEventListener("mouseleave", onLeave);
      card.addEventListener("mouseenter", onEnter);
      cleanups.push(function() {
        card.removeEventListener("mousemove", onMove);
        card.removeEventListener("mouseleave", onLeave);
        card.removeEventListener("mouseenter", onEnter);
      });
    });
    return function() { cleanups.forEach(function(fn) { fn(); }); };
  }, []);

  // Use Sanity stats if available, else fallback
  var rawStats = (settings && settings.heroStats && settings.heroStats.length > 0) ? settings.heroStats : null;
  var stats = rawStats
    ? rawStats.map(function(s) { return { num: s.value, label: s.label }; })
    : fallbackStats;

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", overflow: "hidden", paddingTop: 70 }}>
        {/* Cursor-tracking glow */}
        <div id="cursor-glow" style={{ position: "fixed", top: 0, left: 0, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(34,167,255,0.07) 0%, rgba(213,45,155,0.04) 40%, transparent 70%)", pointerEvents: "none", zIndex: 0, willChange: "transform" }}/>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(34,167,255,0.07) 0%, transparent 60%)" }}/>
        <div style={{ position: "absolute", top: "8%", left: "2%", width: 560, height: 560, borderRadius: "60% 40% 70% 30% / 50% 60% 40% 50%", background: "radial-gradient(circle, rgba(34,167,255,0.09) 0%, rgba(22,88,149,0.05) 50%, transparent 70%)", animation: "blobPulse 9s ease-in-out infinite", filter: "blur(50px)" }}/>
        <div style={{ position: "absolute", bottom: "4%", right: "2%", width: 460, height: 460, borderRadius: "40% 60% 30% 70% / 60% 40% 50% 50%", background: "radial-gradient(circle, rgba(213,45,155,0.08) 0%, rgba(247,153,100,0.06) 50%, transparent 70%)", animation: "blobPulse2 11s ease-in-out infinite", filter: "blur(50px)" }}/>
        <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.5 }}/>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px", position: "relative", zIndex: 2, width: "100%", textAlign: "center" }}>
          <h1 className="fade-up-1" style={{ fontSize: "clamp(36px,6vw,76px)", fontWeight: 900, lineHeight: 1.06, letterSpacing: "-3px", marginBottom: 24, color: "#04100D" }}>
            {(function() {
              var headline = (settings && settings.heroHeadline) ? settings.heroHeadline : "Agentic AI for a Sustainable Built Environment.";
              var words = headline.trim().split(" ");
              var half = Math.ceil(words.length / 2);
              var firstLine = words.slice(0, half).join(" ");
              var secondLine = words.slice(half).join(" ");
              return secondLine ? (
                <span>
                  {firstLine}
                  <br />
                  <span className="gradient-text">{secondLine}</span>
                </span>
              ) : <span className="gradient-text">{firstLine}</span>;
            })()}
          </h1>

          <p className="fade-up-2" style={{ fontSize: "clamp(17px,2vw,21px)", color: "#4a5568", lineHeight: 1.7, maxWidth: 620, margin: "0 auto 40px" }}>
            {(settings && settings.heroSubheadline) ? settings.heroSubheadline : "No templates, no mapping. Automatically extract, calculate, and benchmark — from utility bills to BIM models."}
          </p>

          <div className="fade-up-3" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://lite.strategicgreen.ai/" target="_blank" rel="noreferrer" className="btn-primary mag-btn" style={{ padding: "15px 34px", borderRadius: 12, fontSize: 16, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Access LITE
            </a>
            <button onClick={function() { setPage("Solutions"); }} className="btn-outline mag-btn" style={{ padding: "15px 34px", borderRadius: 12, fontSize: 16 }}>
              Explore Solutions
            </button>
          </div>

          {/* Agentic AI capability tags */}
          <div className="fade-up-4" style={{ marginTop: 44, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", maxWidth: 780, margin: "44px auto 0" }}>
            {[
              { label: "GHG Scope 1, 2 & 3",           color: "#22A7FF", bg: "rgba(34,167,255,0.11)",  border: "rgba(34,167,255,0.35)",  glow: "rgba(34,167,255,0.18)",  delay: "0.5s" },
              { label: "Embodied Carbon Calculation",   color: "#165895", bg: "rgba(22,88,149,0.1)",   border: "rgba(22,88,149,0.3)",    glow: "rgba(22,88,149,0.16)",   delay: "0.62s" },
              { label: "Life Cycle Assessment (LCA)",   color: "#b5226e", bg: "rgba(213,45,155,0.09)", border: "rgba(213,45,155,0.3)",   glow: "rgba(213,45,155,0.15)",  delay: "0.74s" },
              { label: "Early Quantity Take-Off",       color: "#b05518", bg: "rgba(247,153,100,0.12)",border: "rgba(247,153,100,0.38)", glow: "rgba(247,153,100,0.18)", delay: "0.86s" },
              { label: "Portfolio Benchmarking",        color: "#165895", bg: "rgba(22,88,149,0.1)",   border: "rgba(22,88,149,0.3)",    glow: "rgba(22,88,149,0.16)",   delay: "0.98s" },
              { label: "Utility Bill Extraction",       color: "#22A7FF", bg: "rgba(34,167,255,0.11)", border: "rgba(34,167,255,0.35)",  glow: "rgba(34,167,255,0.18)",  delay: "1.1s"  }
            ].map(function(tag) {
              return (
                <span key={tag.label} className="cap-tag"
                  style={{
                    background: tag.bg,
                    border: "1.5px solid " + tag.border,
                    color: tag.color,
                    boxShadow: "0 4px 18px " + tag.glow + ", inset 0 1px 0 rgba(255,255,255,0.5)",
                    animation: "tagIn 0.55s " + tag.delay + " cubic-bezier(0.34,1.56,0.64,1) both"
                  }}>
                  <span className="dot" style={{ background: tag.color, animationDelay: tag.delay }}/>
                  {tag.label}
                </span>
              );
            })}
          </div>

          {/* Stats row */}
          <div className="fade-up-4" style={{ marginTop: 72, display: "flex", gap: 0, justifyContent: "center", flexWrap: "wrap", border: "1px solid #e2e8f0", borderRadius: 20, overflow: "hidden", background: "#fff", boxShadow: "0 8px 40px rgba(0,0,0,0.06)" }}>
            {stats.map(function(s, i) {
              return (
                <div key={s.label} style={{ flex: "1 1 120px", padding: "28px 20px", textAlign: "center", borderRight: i < stats.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <div style={{ fontSize: "clamp(22px,3vw,34px)", fontWeight: 900, letterSpacing: "-1px", lineHeight: 1, color: "#04100D" }}>{s.num}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 6, lineHeight: 1.4 }}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE DEMO ── */}
      <ProductDemoFlow />

      {/* ── SOLUTION INTRO ── */}
      <section style={{ padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Three Solutions</div>
          <h2 style={{ fontSize: "clamp(26px,4vw,50px)", fontWeight: 900, letterSpacing: "-2px", marginBottom: 16, color: "#04100D" }}>
            From utility bills to BIM —
            <br />
            <span className="gradient-text">AI that understands your data.</span>
          </h2>
          <p style={{ fontSize: 18, color: "#64748b", lineHeight: 1.8, maxWidth: 640, margin: "0 auto 48px" }}>
            Utility data extraction for carbon accounting and benchmarking. BIM and 2D drawing AI for embodied carbon and early quantity take-off. All powered by Agentic AI and Vision Language Models.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 20, marginBottom: 48, textAlign: "left" }}>
            {[
              { num: "01", title: "StrategicGreen.ai",    desc: "GHG Scope 1, 2 & 3 carbon calculation, portfolio dashboards, and an AI chatbot.", color: "#165895" },
              { num: "02", title: "StrategicGreen.ai LITE",            desc: "AI VLM extraction from any utility bill. Download clean Excel data instantly.", color: "#22A7FF" },
              { num: "03", title: "BIM & 2D Drawing AI",     desc: "Material inference from BIM or drawings for embodied carbon and quantity take-off.", color: "#D52D9B" }
            ].map(function(s) {
              return (
                <div key={s.num} className="card-hover tilt-card" style={{ borderRadius: 16, border: "1px solid #e2e8f0", background: "#fff", padding: "28px", willChange: "transform" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: s.color, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Solution {s.num}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#04100D", marginBottom: 10 }}>{s.title}</div>
                  <p style={{ fontSize: 13.5, color: "#64748b", lineHeight: 1.7 }}>{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={function() { setPage("Solutions"); }} className="btn-primary" style={{ padding: "13px 30px", borderRadius: 11, fontSize: 15 }}>
              Explore All Solutions
            </button>
            <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=StrategicGreen.ai%20Demo%20Inquiry" target="_blank" rel="noreferrer"
              className="btn-outline" style={{ padding: "13px 30px", borderRadius: 11, fontSize: 15 }}>
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      {/* ── MARKETING VIDEO ── */}
      <VideoSection />

      {/* ── DARK CTA BAND ── */}
      <section style={{ background: "#04100D", padding: "80px 24px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(26px,4vw,44px)", fontWeight: 900, letterSpacing: "-1.5px", color: "#fff", marginBottom: 16 }}>
            Stress-free.{" "}
            <span className="gradient-text">Accurate data.</span>
          </h2>
          <p style={{ color: "#64748b", fontSize: 17, lineHeight: 1.7, marginBottom: 36 }}>
            Join organisations already transforming their utility and carbon data workflows with StrategicGreen.ai.
          </p>
          <a href="https://lite.strategicgreen.ai/" target="_blank" rel="noreferrer" className="btn-gradient" style={{ padding: "15px 36px", borderRadius: 12, fontSize: 16, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            Access LITE →
          </a>
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SOLUTIONS PAGE
// ═══════════════════════════════════════════════════════════════════════════
function CountUp(props) {
  var target = props.target;
  var suffix = props.suffix || "";
  var prefix = props.prefix || "";
  var duration = props.duration || 1400;
  var valState = useState(0);
  var val = valState[0];
  var setVal = valState[1];
  useEffect(function() {
    var el = document.getElementById(props.id);
    if (!el) return;
    var observer = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting) {
        var start = 0;
        var startTime = null;
        function step(ts) {
          if (!startTime) startTime = ts;
          var progress = Math.min((ts - startTime) / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          setVal(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
          else setVal(target);
        }
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return function() { observer.disconnect(); };
  }, [target, duration, props.id]);

  return (
    <span id={props.id}>{prefix}{val}{suffix}</span>
  );
}

function SolutionsPage(props) {
  var setPage = props.setPage;
  var tabState = useState(0);
  var tab = tabState[0];
  var setTab = tabState[1];

  // 3D tilt on solution cards
  useEffect(function() {
    var cards = document.querySelectorAll(".tilt-card");
    var cleanups = [];
    cards.forEach(function(card) {
      function onMove(e) {
        var r = card.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = "perspective(600px) rotateY(" + (x * 10) + "deg) rotateX(" + (-y * 10) + "deg) translateZ(6px)";
      }
      function onLeave() {
        card.style.transform = "perspective(600px) rotateY(0deg) rotateX(0deg) translateZ(0px)";
        card.style.transition = "transform 0.5s cubic-bezier(0.34,1.2,0.64,1)";
      }
      function onEnter() { card.style.transition = "transform 0.1s ease"; }
      card.addEventListener("mousemove", onMove);
      card.addEventListener("mouseleave", onLeave);
      card.addEventListener("mouseenter", onEnter);
      cleanups.push(function() {
        card.removeEventListener("mousemove", onMove);
        card.removeEventListener("mouseleave", onLeave);
        card.removeEventListener("mouseenter", onEnter);
      });
    });
    return function() { cleanups.forEach(function(fn) { fn(); }); };
  }, [tab]);

  var solutions = [
    {
      id: "01", label: "StrategicGreen.ai", sublabel: "Carbon Intelligence",
      color: "#165895", colorLight: "rgba(22,88,149,0.07)",
      badge: "Most Advanced",
      headline: "Turn utility bills into carbon intelligence.",
      tagline: "GHG Scope 1, 2 & 3 accounting, portfolio dashboards, and an AI chatbot — automatically, from your raw utility data.",
      outcomes: [
        { num: 94, suffix: "%", label: "Faster reporting", context: "vs. manual processing" },
        { num: 10, suffix: "×", label: "ROI on average",  context: "across client portfolios" },
        { num: 3,  suffix: "",  label: "GHG Scopes",      context: "calculated automatically" },
        { num: 100, suffix: "%", label: "Accuracy",       context: "up to, zero manual checks" }
      ],
      proof: [
        "AI VLM extraction from any utility bill format",
        "Scope 1, 2 & 3 auto-calculated per asset",
        "Portfolio benchmarking across your entire building portfolio",
        "AI chatbot surfaces insights on demand",
        "Standardised monthly view across all billing cycles"
      ],
      cta: "Book a Demo", ctaAction: "demo",
      cta2: "View Pricing", cta2Action: "pricing"
    },
    {
      id: "02", label: "StrategicGreen.ai LITE", sublabel: "Data Extraction",
      color: "#22A7FF", colorLight: "rgba(34,167,255,0.06)",
      badge: "Start Here",
      headline: "Any bill. Clean data. In seconds.",
      tagline: "Drop in any utility bill — any format, any language. Our AI VLM reads it like a human and hands you clean Excel data instantly.",
      outcomes: [
        { num: 94,  suffix: "%", label: "Time saved",      context: "vs. manual data entry" },
        { num: 100, suffix: "%", label: "Format coverage",  context: "PDF, JPG, PNG — any layout" },
        { num: 8,   suffix: "",  label: "Languages",        context: "supported out of the box" },
        { num: 2000, suffix: "+", label: "Bills tested",    context: "across real client data" }
      ],
      proof: [
        "No templates, no mapping — zero setup",
        "Any utility type and billing period",
        "Bulk upload — process hundreds at once",
        "Download results in Excel instantly",
        "Cloud-based — works from any device"
      ],
      cta: "Access LITE", ctaAction: "earlyaccess",
      cta2: "Learn More", cta2Action: "demo"
    },
    {
      id: "03", label: "BIM & 2D Drawing AI", sublabel: "Material Inference",
      color: "#D52D9B", colorLight: "rgba(213,45,155,0.06)",
      badge: "Coming Soon",
      headline: "Incomplete model? AI fills the gaps.",
      tagline: "Our AI reads your BIM files or 2D drawings, infers missing materials, and generates embodied carbon or early quantity take-off reports.",
      outcomes: [
        { num: 90,  suffix: "%", label: "Time reduction",   context: "for quantity take-off" },
        { num: 100, suffix: "%", label: "Model coverage",   context: "BIM and 2D drawings" },
        { num: 0,   suffix: "",  label: "Manual mapping",   context: "AI infers everything" },
        { num: 2,   suffix: "",  label: "Use cases",        context: "Embodied carbon + QTO" }
      ],
      proof: [
        "BIM files and 2D drawings — both supported",
        "AI infers missing materials automatically",
        "Embodied carbon & full LCA reports",
        "Early-stage quantity take-off in minutes",
        "Dramatically reduces pre-design estimation time"
      ],
      cta: "Enquire About BIM AI", ctaAction: "bim",
      cta2: null, cta2Action: null
    }
  ];

  var sol = solutions[tab];

  return (
    <div style={{ paddingTop: 70 }}>

      {/* ── HERO ── */}
      <section style={{ padding: "72px 24px 0", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.3 }}/>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(22,88,149,0.06) 0%, transparent 70%)" }}/>
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
          <div className="section-label fade-up" style={{ marginBottom: 16 }}>Our Solutions</div>
          <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,58px)", fontWeight: 800, letterSpacing: "-2px", lineHeight: 1.06, color: "#04100D", marginBottom: 16 }}>
            Real outcomes.<br/><span className="gradient-text">Measurable results.</span>
          </h1>
          <p className="fade-up-2" style={{ fontSize: 16, color: "#64748b", marginBottom: 48 }}>
            Three AI solutions built for the built environment — each designed to eliminate manual work and surface carbon intelligence at speed.
          </p>
        </div>

        {/* ── TAB SELECTOR ── */}
        <div style={{ position: "sticky", top: 70, zIndex: 50, background: "rgba(255,255,255,0.96)", backdropFilter: "blur(14px)", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
          <div className="solutions-tabs" style={{ maxWidth: 860, margin: "0 auto", display: "flex" }}>
            {solutions.map(function(s, i) {
              var isActive = i === tab;
              return (
                <button key={s.id} onClick={function() { setTab(i); }}
                  style={{ flex: 1, padding: "16px 10px 14px", background: "none", border: "none", cursor: "pointer",
                    borderBottom: isActive ? "3px solid " + s.color : "3px solid transparent",
                    fontFamily: "Google Sans, sans-serif", transition: "all 0.2s ease",
                    position: "relative" }}>
                  {i === 0 && (
                    <div style={{ position: "absolute", top: 8, right: 8, fontSize: 9, fontWeight: 800, color: "#fff",
                      background: "linear-gradient(135deg,#22A7FF,#165895)", borderRadius: 4, padding: "2px 5px", letterSpacing: "0.06em" }}>
                      PRO
                    </div>
                  )}
                  <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? s.color : "#cbd5e1", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 3 }}>{s.id}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#04100D" : "#94a3b8", lineHeight: 1.3 }}>{s.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SOLUTION DETAIL PANEL ── */}
      <section style={{ padding: "64px 24px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>

          {/* Headline row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start", marginBottom: 56 }} className="two-col">
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 16,
                background: sol.colorLight, border: "1px solid " + sol.color + "30",
                borderRadius: 8, padding: "5px 12px" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: sol.color }}/>
                <span style={{ fontSize: 11, fontWeight: 700, color: sol.color, letterSpacing: "0.08em", textTransform: "uppercase" }}>{sol.badge}</span>
              </div>
              <h2 style={{ fontSize: "clamp(24px,3.5vw,42px)", fontWeight: 900, letterSpacing: "-1.5px", lineHeight: 1.06, color: "#04100D", marginBottom: 16 }}>
                {sol.headline}
              </h2>
              <p style={{ fontSize: 16, color: "#64748b", lineHeight: 1.8 }}>{sol.tagline}</p>
            </div>

            {/* Proof list */}
            <div style={{ background: "#fafbfc", borderRadius: 20, border: "1px solid #e2e8f0", padding: "28px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>What you get</div>
              {sol.proof.map(function(p) {
                return (
                  <div key={p} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                      <circle cx="10" cy="10" r="10" fill={sol.color + "18"}/>
                      <path d="M6 10.5L8.5 13L14 7.5" stroke={sol.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.6, fontWeight: 500 }}>{p}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Outcome metrics — 4 big number cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 48 }} className="stats-row">
            {sol.outcomes.map(function(o, i) {
              var uid = "cu-" + tab + "-" + i;
              return (
                <div key={i} className="tilt-card" style={{ borderRadius: 20, background: "#fff", border: "1px solid #e2e8f0", padding: "32px 20px", textAlign: "center", willChange: "transform" }}>
                  <div style={{ fontSize: "clamp(32px,4vw,52px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1, color: sol.color, marginBottom: 10 }}>
                    <CountUp id={uid} target={o.num} suffix={o.suffix} duration={1200}/>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#04100D", marginBottom: 4 }}>{o.label}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>{o.context}</div>
                </div>
              );
            })}
          </div>

          {/* CTA row */}
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", paddingBottom: 80 }}>
            {sol.ctaAction === "demo" && (
              <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=StrategicGreen.ai%20Demo%20Inquiry" target="_blank" rel="noreferrer" className="btn-primary mag-btn" style={{ padding: "14px 34px", borderRadius: 12, fontSize: 15, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{sol.cta}</a>
            )}
            {sol.ctaAction === "earlyaccess" && (
              <a href="https://lite.strategicgreen.ai/" target="_blank" rel="noreferrer" className="btn-primary mag-btn" style={{ padding: "14px 34px", borderRadius: 12, fontSize: 15, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{sol.cta}</a>
            )}
            {sol.ctaAction === "bim" && (
              <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=BIM+AI+Inquiry" target="_blank" rel="noreferrer" className="btn-primary mag-btn" style={{ padding: "14px 34px", borderRadius: 12, fontSize: 15, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{sol.cta}</a>
            )}
            {sol.cta2 && sol.cta2Action === "pricing" && (
              <button onClick={function() { setPage("Pricing"); }} className="btn-outline mag-btn" style={{ padding: "14px 34px", borderRadius: 12, fontSize: 15 }}>{sol.cta2}</button>
            )}
            {sol.cta2 && sol.cta2Action === "demo" && (
              <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=StrategicGreen.ai%20Demo%20Inquiry" target="_blank" rel="noreferrer" className="btn-outline mag-btn" style={{ padding: "14px 34px", borderRadius: 12, fontSize: 15 }}>{sol.cta2}</a>
            )}
          </div>
        </div>
      </section>

      {/* ── ALL 3 SOLUTIONS COMPARISON STRIP ── */}
      <section style={{ padding: "0 24px 80px", background: "#fafbfc", borderTop: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", paddingTop: 56 }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div className="section-label" style={{ marginBottom: 10 }}>Compare Solutions</div>
            <h3 style={{ fontSize: "clamp(20px,3vw,30px)", fontWeight: 800, letterSpacing: "-1px", color: "#04100D" }}>Pick the right fit for your team</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }} className="product-row">
            {solutions.map(function(s, i) {
              var isActive = i === tab;
              return (
                <button key={s.id} onClick={function() { setTab(i); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                  className="tilt-card"
                  style={{ borderRadius: 20, border: "2px solid " + (isActive ? s.color : "#e2e8f0"),
                    background: isActive ? s.color + "08" : "#fff",
                    padding: "28px 22px", textAlign: "left", cursor: "pointer",
                    fontFamily: "Google Sans, sans-serif", transition: "border-color 0.25s ease, background 0.25s ease",
                    boxShadow: isActive ? "0 10px 32px " + s.color + "22" : "none",
                    willChange: "transform" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: s.color, letterSpacing: "0.12em", textTransform: "uppercase" }}>Solution {s.id}</div>
                    {i === 0 && <div style={{ fontSize: 9, fontWeight: 800, color: "#fff", background: "linear-gradient(135deg,#22A7FF,#165895)", borderRadius: 4, padding: "2px 6px" }}>PRO</div>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#04100D", marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>{s.sublabel}</div>
                  <div style={{ fontSize: 12, color: isActive ? s.color : "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {isActive ? "Currently viewing" : "View solution"}
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke={isActive ? s.color : "#94a3b8"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CHALLENGES ── */}
      <section style={{ padding: "80px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="section-label" style={{ marginBottom: 14 }}>The Problem</div>
            <h2 style={{ fontSize: "clamp(22px,3.5vw,38px)", fontWeight: 800, letterSpacing: "-1.5px", color: "#04100D" }}>
              Challenges of Utility Data Management
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 24 }}>
            {[
              { icon: "clock",    title: "Time-Consuming",         desc: "Manually extracting data from hundreds of utility bills across different formats takes weeks, not hours." },
              { icon: "error",    title: "Error-Prone",            desc: "Manual data entry introduces inconsistencies and errors that undermine your carbon reporting accuracy." },
              { icon: "globe",    title: "Multi-Format Chaos",     desc: "Bills arrive as PDFs, images, and spreadsheets in dozens of languages and layouts — impossible to standardise manually." },
              { icon: "chart",    title: "No Portfolio View",      desc: "Without automation, aggregating consumption data across a portfolio for benchmarking and trend analysis is impractical." }
            ].map(function(c) {
              return (
                <div key={c.title} className="card-hover tilt-card" style={{ background: "#fafbfc", borderRadius: 18, padding: "32px", border: "1px solid #e2e8f0", willChange: "transform" }}>
                  <GradIcon name={c.icon} size={44} />
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10, color: "#04100D", marginTop: 14 }}>{c.title}</h3>
                  <p style={{ fontSize: 13.5, color: "#64748b", lineHeight: 1.75 }}>{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRICING PAGE
// ═══════════════════════════════════════════════════════════════════════════
function PricingPage(props) {
  var billingState = useState("yearly");
  var billing = billingState[0];
  var setBilling = billingState[1];

  var pricingResult = useSanity('*[_type == "siteSettings"][0]{litePriceYearly,litePriceMonthly,liteFeatures,proFeatures}', null);
  var pricingSettings = pricingResult[0];

  var litePriceYearly = (pricingSettings && pricingSettings.litePriceYearly) ? pricingSettings.litePriceYearly : "$200";
  var litePriceMonthly = (pricingSettings && pricingSettings.litePriceMonthly) ? pricingSettings.litePriceMonthly : "$20";
  var litePriceYearlyNum = parseInt(litePriceYearly.replace(/[^0-9]/g,"")) || 200;
  var litePriceMonthlyNum = parseInt(litePriceMonthly.replace(/[^0-9]/g,"")) || 20;

  var defaultLiteFeatures = ["250 pages (PDF, JPG, PNG)","Cloud service","Export to Excel","Option to purchase more pages"];
  var defaultLiteYearlyFeatures = ["Everything in Monthly","250 pages (PDF, JPG, PNG)","Cloud service","Export to Excel","Roll over unused pages monthly"];
  var defaultProFeatures = ["5,000 pages (PDF, JPG, PNG)","Cloud service / local storage","Portfolio benchmarking","Dedicated support & training","Carbon accounting dashboards","Custom deployment options"];

  var activeLiteFeatures = (pricingSettings && pricingSettings.liteFeatures && pricingSettings.liteFeatures.length > 0) ? pricingSettings.liteFeatures : (billing === "yearly" ? defaultLiteYearlyFeatures : defaultLiteFeatures);
  var activeProFeatures = (pricingSettings && pricingSettings.proFeatures && pricingSettings.proFeatures.length > 0) ? pricingSettings.proFeatures : defaultProFeatures;

  var liteMonthly = {
    name: "LITE", billing: "Monthly",
    price: litePriceMonthlyNum, period: "/ month",
    badge: "Limited Offer", featured: false,
    cta: "Access LITE",
    ctaHref: "https://lite.strategicgreen.ai/",
    desc: "Everything you need to extract clean structured data from utility bills.",
    features: activeLiteFeatures
  };

  var liteYearly = {
    name: "LITE", billing: "Yearly",
    price: litePriceYearlyNum, period: "/ year",
    badge: "Limited Offer", featured: true,
    cta: "Access LITE",
    ctaHref: "https://lite.strategicgreen.ai/",
    desc: "Best value for teams committed to streamlining utility data year-round.",
    features: activeLiteFeatures
  };

  var professional = {
    name: "Professional", billing: null,
    price: null, period: null,
    badge: null, featured: false,
    cta: "Book a Demo",
    ctaHref: "https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=StrategicGreen.ai%20Demo%20Inquiry",
    desc: "AI-powered extraction for carbon accounting and portfolio-wide sustainability benchmarking.",
    features: activeProFeatures
  };

  var liteCard = billing === "yearly" ? liteYearly : liteMonthly;
  var cards = [liteCard, professional];

  return (
    <div style={{ paddingTop: 70 }}>
      <section style={{ padding: "80px 24px 40px", textAlign: "center" }}>
        <div className="section-label fade-up" style={{ marginBottom: 16 }}>Pricing</div>
        <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,56px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.06, marginBottom: 16, color: "#04100D" }}>
          Simple, transparent pricing.
          <br />
          <span className="gradient-text">Start for free.</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: 17, color: "#64748b", lineHeight: 1.7, marginBottom: 40 }}>
          Choose the plan that fits your workflow. Upgrade to Professional as your portfolio grows.
        </p>

        {/* ── BILLING TOGGLE ── */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 0, background: "#f1f5f9", borderRadius: 14, padding: 4, marginBottom: 48 }}>
          <button onClick={function() { setBilling("yearly"); }}
            style={{ padding: "9px 24px", borderRadius: 11, border: "none", cursor: "pointer", fontFamily: "Google Sans, sans-serif",
              fontSize: 14, fontWeight: 700, transition: "all 0.22s ease",
              background: billing === "yearly" ? "#04100D" : "transparent",
              color: billing === "yearly" ? "#fff" : "#64748b" }}>
            Yearly
            <span style={{ marginLeft: 7, fontSize: 11, fontWeight: 700,
              color: billing === "yearly" ? "#22A7FF" : "#94a3b8",
              background: billing === "yearly" ? "rgba(34,167,255,0.18)" : "transparent",
              borderRadius: 6, padding: billing === "yearly" ? "2px 6px" : "0" }}>
              SAVE 17%
            </span>
          </button>
          <button onClick={function() { setBilling("monthly"); }}
            style={{ padding: "9px 24px", borderRadius: 11, border: "none", cursor: "pointer", fontFamily: "Google Sans, sans-serif",
              fontSize: 14, fontWeight: 700, transition: "all 0.22s ease",
              background: billing === "monthly" ? "#04100D" : "transparent",
              color: billing === "monthly" ? "#fff" : "#64748b" }}>
            Monthly
          </button>
        </div>

        {/* ── CARDS ── */}
        <div className="two-col pricing-grid" style={{ maxWidth: 760, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {cards.map(function(plan) {
            return (
              <div key={plan.name + (plan.billing || "")} className={"plan-card" + (plan.featured ? " featured" : "")} style={{ textAlign: "left" }}>
                {plan.featured && (
                  <div style={{ position: "absolute", top: -15, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
                    <span className="btn-gradient" style={{ padding: "4px 18px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.07em" }}>BEST VALUE</span>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.09em" }}>{plan.name}</div>
                  {plan.billing && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f1f5f9", borderRadius: 6, padding: "3px 8px", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {plan.billing}
                    </div>
                  )}
                </div>

                {plan.price ? (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: 24, color: "#04100D", fontWeight: 700, marginRight: 0 }}>$</span>
                    <span style={{ fontSize: 52, fontWeight: 900, letterSpacing: "-2px", lineHeight: 1, color: "#04100D" }}>{plan.price}</span>
                    <span style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.3, whiteSpace: "nowrap" }}>{plan.period}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 36, fontWeight: 900, marginBottom: 4, color: "#04100D" }}>Contact Us</div>
                )}

                {plan.badge && (
                  <div style={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: "#fff",
                    background: "linear-gradient(135deg, #22A7FF, #D52D9B)", padding: "3px 12px",
                    borderRadius: 20, marginBottom: 10, letterSpacing: "0.05em", textTransform: "uppercase",
                    alignSelf: "flex-start" }}>{plan.badge}</div>
                )}

                <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, marginBottom: 20, borderBottom: "1px solid #e2e8f0", paddingBottom: 18 }}>{plan.desc}</p>

                <div style={{ marginBottom: 28, flex: 1 }}>
                  {plan.features.map(function(f) {
                    return (
                      <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 11 }}>
                        <GradCheck />
                        <span style={{ fontSize: 13.5, color: "#4a5568", lineHeight: 1.5 }}>{f}</span>
                      </div>
                    );
                  })}
                </div>

                {plan.ctaHref ? (
                  <a href={plan.ctaHref} target="_blank" rel="noreferrer"
                    className={plan.featured ? "btn-gradient" : "btn-primary"}
                    style={{ width: "100%", padding: "13px", borderRadius: 10, fontSize: 15, textAlign: "center", display: "block" }}>
                    {plan.cta}
                  </a>
                ) : (
                  <button onClick={function() { props.setEarlyAccessOpen(true); }}
                    className={plan.featured ? "btn-gradient" : "btn-primary"}
                    style={{ width: "100%", padding: "13px", borderRadius: 10, fontSize: 15, textAlign: "center", display: "block", border: "none", cursor: "pointer", fontFamily: "Google Sans, sans-serif" }}>
                    {plan.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* All plans include */}
      <section style={{ padding: "60px 24px 80px", background: "#fafbfc", borderTop: "1px solid #e2e8f0", marginTop: 64 }}>
        <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 32 }}>All plans include</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px,1fr))", gap: 14 }}>
            {["Cloud-based platform","Web interface","No installation required","Secure data handling","Dedicated AWS infrastructure available","Multi-language bill support"].map(function(f) {
              return (
                <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px" }}>
                  <GradCheck />
                  <span style={{ fontSize: 13.5, color: "#4a5568", fontWeight: 500, lineHeight: 1.4 }}>{f}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ABOUT PAGE
// ═══════════════════════════════════════════════════════════════════════════
function AboutPage() {
  var stats = [
    ["2,000+", "Bills tested"],
    ["8",      "Languages"],
    ["10×",   "ROI"],
    ["94%",   "Faster with AI"],
    ["100%",  "Accuracy (up to)"]
  ];

  var fallbackTeam = [
    { name: "Dr. Calvin Kam",   initials: "CK", c1: "#22A7FF", c2: "#165895" },
    { name: "Edwin Jeu",        initials: "EJ", c1: "#165895", c2: "#D52D9B" },
    { name: "Janice Do",        initials: "JD", c1: "#D52D9B", c2: "#F79964" },
    { name: "Kayla Wong",       initials: "KW", c1: "#F79964", c2: "#22A7FF" },
    { name: "Jonathan Widney",  initials: "JW", c1: "#22A7FF", c2: "#D52D9B" },
    { name: "Dr. Min Song",     initials: "MS", c1: "#D52D9B", c2: "#165895" }
  ];

  var teamColors = ["#22A7FF","#165895","#D52D9B","#F79964"];
  var teamColors2 = ["#165895","#D52D9B","#F79964","#22A7FF"];

  var teamResult = useSanity('*[_type == "siteSettings"][0]{teamMembers[]{name,initials,title,bio,photo{...,asset->{_id,url,_ref}}}}', null);
  var teamSettings = teamResult[0];
  var team = (teamSettings && teamSettings.teamMembers && teamSettings.teamMembers.length > 0)
    ? teamSettings.teamMembers.map(function(m, idx) {
        return {
          name: m.name,
          initials: m.initials || (m.name || "").split(" ").map(function(w) { return w[0]; }).join("").substring(0,2).toUpperCase(),
          title: m.title, bio: m.bio,
          photo: m.photo ? sanityImg(m.photo, 240) : null,
          c1: teamColors[idx % 4], c2: teamColors2[idx % 4]
        };
      })
    : fallbackTeam;

  return (
    <div style={{ paddingTop: 70 }}>
      {/* Mission */}
      <section style={{ padding: "80px 24px 64px", maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
        <div className="section-label fade-up" style={{ marginBottom: 16 }}>About Us</div>
        <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,54px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.06, marginBottom: 24, color: "#04100D" }}>
          Making sustainability performance
          <br />
          <span className="gradient-text">measurable, automated, and decision-ready.</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: 18, color: "#64748b", lineHeight: 1.85 }}>
          StrategicGreen.ai is a built-environment intelligence startup designed to transform how organisations understand, manage, and act on sustainability and utility data. We bring clarity, speed, and operational insight to energy, carbon, water, and cost signals across buildings and portfolios.
        </p>
      </section>

      {/* Proven experience */}
      <section style={{ background: "#fafbfc", borderTop: "1px solid #e2e8f0", padding: "80px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "start" }} className="two-col">
          <div>
            <div className="section-label" style={{ marginBottom: 14 }}>Our Foundation</div>
            <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px", marginBottom: 18, color: "#04100D" }}>
              Built on Proven Industry Experience
            </h2>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85, marginBottom: 16 }}>
              StrategicGreen.ai was launched by the team behind <strong>Strategic Building Innovation (SBI)</strong>, an internationally recognised firm with over a decade of experience advancing innovation, sustainability, and performance across the built environment.
            </p>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85, marginBottom: 16 }}>
              Since 2011, SBI has worked globally across financial, commercial, residential, and hospitality sectors, helping organisations navigate complex building data, operational inefficiencies, and sustainability challenges.
            </p>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85 }}>
              This lineage gives StrategicGreen.ai a credible foundation grounded in deep domain knowledge of building performance, process optimisation, data standards, and sustainability outcomes.
            </p>
          </div>
          <div>
            <div className="section-label" style={{ marginBottom: 14 }}>Validated</div>
            <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px", marginBottom: 18, color: "#04100D" }}>
              Proven in the Real World
            </h2>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85, marginBottom: 16 }}>
              StrategicGreen.ai has been validated through real-world implementations across diverse asset classes and international contexts. The platform handles complex, multilingual utility data while delivering measurable improvements in speed, accuracy, and insight generation.
            </p>
            <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85 }}>
              By bridging practical industry knowledge with modern AI capabilities, StrategicGreen.ai enables organisations to move beyond spreadsheets and manual processes toward scalable, data-driven sustainability management.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: "#04100D", padding: "64px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0 }}>
          {stats.map(function(s, i) {
            return (
              <div key={s[1]} style={{ flex: "1 1 160px", textAlign: "center", padding: "28px 20px", borderRight: i < stats.length - 1 ? "1px solid #1e2d42" : "none" }}>
                <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-2px", color: "#fff" }}>{s[0]}</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{s[1]}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Team */}
      <section style={{ padding: "80px 24px", maxWidth: 1000, margin: "0 auto" }}>
        <div className="section-label" style={{ marginBottom: 16, textAlign: "center" }}>The Team</div>
        <h2 style={{ fontSize: "clamp(24px,3vw,38px)", fontWeight: 800, textAlign: "center", marginBottom: 48, letterSpacing: "-1px" }}>Our Team</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 24 }}>
          {team.map(function(t) {
            return (
              <div key={t.name} className="card-hover" style={{ textAlign: "center", padding: "28px 20px", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                {t.photo
                  ? <img src={t.photo} alt={t.name} style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", margin: "0 auto 16px", display: "block", border: "2px solid #e2e8f0" }} />
                  : <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg," + t.c1 + "," + t.c2 + ")", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, fontWeight: 700, margin: "0 auto 16px" }}>{t.initials}</div>
                }
                <div style={{ fontWeight: 700, fontSize: 15, color: "#04100D" }}>{t.name}</div>
                {t.title && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{t.title}</div>}
                {t.bio && <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 8, lineHeight: 1.6 }}>{t.bio}</div>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FAQ PAGE
// ═══════════════════════════════════════════════════════════════════════════
function FAQPage() {
  var openState = useState(null);
  var open = openState[0];
  var setOpen = openState[1];

  var fallbackFaqs = [
    {
      q: "What is the difference between LITE and Professional?",
      a: "LITE will extract data from your utility bills which you can export into Excel. Professional turns that data into insights on a full dashboard display — including carbon accounting, portfolio benchmarking, and standardised monthly reporting."
    },
    {
      q: "Is StrategicGreen.ai cloud-based?",
      a: "Yes, StrategicGreen.ai is a cloud-based platform with a web interface. No installation is required — you can access it directly from your browser."
    },
    {
      q: "Can it be used on a private cloud or does it need to be on StrategicGreen.ai's cloud?",
      a: "The StrategicGreen.ai platform can be deployed on a private cloud. Contact us to discuss the details for this request."
    },
    {
      q: "If I use StrategicGreen.ai's cloud, how will my profile be separated from others'?",
      a: "Dedicated AWS infrastructure (servers, storage, etc.) can be deployed at a preferred location — for example US, Singapore, Hong Kong, Europe — if required."
    },
    {
      q: "What bill types and formats does the platform support?",
      a: "The platform supports utility bills in PDF, JPG, and PNG formats. It handles any utility type (electricity, gas, water, and more) across any billing format — no templates or manual mapping required."
    },
    {
      q: "How many languages are supported?",
      a: "StrategicGreen.ai currently supports 8 languages, enabling organisations with international portfolios to process bills from different regions without manual translation or mapping."
    },
    {
      q: "What does 'up to 100% accuracy' mean?",
      a: "Our AI extraction engine achieves very high accuracy on structured utility bills. The 'up to 100%' figure reflects best-case performance on clean, well-formatted documents. Accuracy may vary for highly unusual or damaged bill formats."
    },
    {
      q: "What does the 10× ROI claim mean?",
      a: "Based on client outcomes, organisations using StrategicGreen.ai recover the cost of the platform many times over through reduced manual labour, faster reporting, and avoided errors — typically achieving a 10× return on investment."
    },
    {
      q: "How do I get started?",
      a: "You can request early access to LITE for free via our website. For Professional, book a demo with our team and we'll walk you through a tailored implementation plan."
    },
    {
      q: "Who is behind StrategicGreen.ai?",
      a: "StrategicGreen.ai was launched by the team behind Strategic Building Innovation (SBI), an internationally recognised firm with over a decade of experience in building performance, sustainability, and data management across global portfolios."
    }
  ];

  var settingsForFaq = useSanity('*[_type == "siteSettings"][0]{faqItems}', null);
  var faqSettings = settingsForFaq[0];
  var faqs = (faqSettings && faqSettings.faqItems && faqSettings.faqItems.length > 0)
    ? faqSettings.faqItems.map(function(f) { return { q: f.question, a: f.answer }; })
    : fallbackFaqs;

  return (
    <div style={{ paddingTop: 70 }}>
      <section style={{ padding: "80px 24px 48px", textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
        <div className="section-label fade-up" style={{ marginBottom: 16 }}>FAQ</div>
        <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.06, marginBottom: 16, color: "#04100D" }}>
          Frequently asked
          <br />
          <span className="gradient-text">questions.</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: 17, color: "#64748b", lineHeight: 1.7 }}>
          Explore common questions about our platform.{" "}
          <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com" target="_blank" rel="noreferrer" style={{ color: "#165895", fontWeight: 600 }}>Still have questions? Email us →</a>
        </p>
      </section>

      <section style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px 100px" }}>
        {faqs.map(function(f, i) {
          return (
            <div key={i} className="faq-item">
              <button className="faq-btn" onClick={function() { setOpen(open === i ? null : i); }}
                style={{ padding: "22px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: "#04100D", textAlign: "left" }}>{f.q}</span>
                <span style={{ fontSize: 24, fontWeight: 300, lineHeight: 1, flexShrink: 0, color: "#04100D" }}>{open === i ? "−" : "+"}</span>
              </button>
              {open === i && (
                <div style={{ paddingBottom: 22, animation: "slideDown 0.2s ease both" }}>
                  <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.85 }}>{f.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BLOG PAGE  (editorial content relevant to the real product focus)
// ═══════════════════════════════════════════════════════════════════════════
function BlogDetailPage(props) {
  var post = props.post;
  var onBack = props.onBack;
  useEffect(function() { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

  var bannerUrl = (post.coverImage && post.showAsBanner !== false) ? sanityImg(post.coverImage, 1400) : null;

  return (
    <div style={{ paddingTop: 70 }}>
      <section style={{ padding: "64px 24px 48px", background: "linear-gradient(180deg,rgba(34,167,255,0.04) 0%,transparent 100%)", position: "relative", overflow: "hidden" }}>
        <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.3 }}/>
        <div style={{ maxWidth: 760, margin: "0 auto", position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "#64748b", fontFamily: "Google Sans, sans-serif", marginBottom: 24, padding: 0 }}
            onMouseEnter={function(e) { e.currentTarget.style.color = "#04100D"; }}
            onMouseLeave={function(e) { e.currentTarget.style.color = "#64748b"; }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Blog
          </button>
          <span className="tag" style={{ marginBottom: 16, display: "inline-block", background: post.tagBg || "rgba(34,167,255,0.1)", color: post.color }}>{post.tag}</span>
          <h1 style={{ fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-1.5px", lineHeight: 1.1, color: "#04100D", marginBottom: 20 }}>{post.title}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#22A7FF,#D52D9B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700 }}>SG</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#04100D" }}>{post.author}</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{post.date} · {post.readTime} read</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {bannerUrl && (
        <div style={{ maxWidth: 1100, margin: "0 auto 48px", padding: "0 24px" }}>
          <img src={bannerUrl} alt={post.title} style={{ width: "100%", maxHeight: 480, objectFit: "cover", borderRadius: 16, display: "block", boxShadow: "0 8px 40px rgba(0,0,0,0.10)" }} />
        </div>
      )}

      <section style={{ maxWidth: 760, margin: "0 auto", padding: "0 24px 80px" }}>
        <p style={{ fontSize: 18, color: "#334155", lineHeight: 1.85, marginBottom: 32, fontWeight: 500 }}>{post.excerpt}</p>
        {(post.body || []).map(function(block, i) {
          if (block.type === "h2") return <h2 key={i} style={{ fontSize: "clamp(18px,2.5vw,24px)", fontWeight: 800, color: "#04100D", letterSpacing: "-0.4px", marginTop: 40, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" }}><RichText children={block.children || [{text: block.text}]} markDefs={block.markDefs || []} /></h2>;
          if (block.type === "h3") return <h3 key={i} style={{ fontSize: "clamp(16px,2vw,20px)", fontWeight: 700, color: "#04100D", letterSpacing: "-0.3px", marginTop: 28, marginBottom: 10 }}><RichText children={block.children || [{text: block.text}]} markDefs={block.markDefs || []} /></h3>;
          if (block.type === "p") return <p key={i} style={{ fontSize: 16, color: "#475569", lineHeight: 1.9, marginBottom: 18 }}><RichText children={block.children || [{text: block.text}]} markDefs={block.markDefs || []} /></p>;
          if (block.type === "callout") return <div key={i} style={{ borderLeft: "3px solid #22A7FF", margin: "28px 0", background: "rgba(34,167,255,0.04)", borderRadius: "0 8px 8px 0", padding: "16px 20px" }}><p style={{ fontSize: 15, color: "#334155", lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>{block.text}</p></div>;
          if (block.type === "list") return <ul key={i} style={{ margin: "16px 0 24px", padding: 0, listStyle: "none" }}>{block.items.map(function(item, j) { return <li key={j} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22A7FF", marginTop: 9, flexShrink: 0 }}/><span style={{ fontSize: 15, color: "#475569", lineHeight: 1.75 }}>{item}</span></li>; })}</ul>;
          if (block.type === "stat") return <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, margin: "32px 0" }}>{block.items.map(function(s, j) { return <div key={j} style={{ background: "#f8fafc", borderRadius: 12, padding: "20px 16px", textAlign: "center" }}><div style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 900, color: "#04100D", letterSpacing: "-1px" }}>{s.value}</div><div style={{ fontSize: 12, color: "#64748b", marginTop: 4, lineHeight: 1.4 }}>{s.label}</div></div>; })}</div>;
          if (block.type === "image") {
            var imgSrc = sanityImg(block, block.size === "small" ? 400 : block.size === "medium" ? 700 : 1100);
            if (!imgSrc) return null;
            var imgStyle = block.size === "full"
              ? { width: "100%", borderRadius: 12, display: "block", margin: "28px 0" }
              : { maxWidth: block.size === "medium" ? 560 : 320, width: "100%", borderRadius: 12, display: "block", margin: "28px auto" };
            return <figure key={i} style={{ margin: 0 }}>
              <img src={imgSrc} alt={block.alt || ""} style={imgStyle} />
              {block.caption && <figcaption style={{ textAlign: "center", fontSize: 13, color: "#94a3b8", marginTop: 8 }}>{block.caption}</figcaption>}
            </figure>;
          }
          if (block.type === "video") {
            var embedUrl = getVideoEmbedUrl(block.url);
            if (!embedUrl) return null;
            return <figure key={i} style={{ margin: "32px 0" }}>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 12, overflow: "hidden", background: "#000" }}>
                <iframe src={embedUrl} title={block.caption || "Video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }} />
              </div>
              {block.caption && <figcaption style={{ textAlign: "center", fontSize: 13, color: "#94a3b8", marginTop: 8 }}>{block.caption}</figcaption>}
            </figure>;
          }
          return null;
        })}
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 32, marginTop: 40, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <button onClick={onBack} className="btn-outline" style={{ padding: "11px 24px", borderRadius: 10, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Blog
          </button>
          <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com&su=Blog%20Enquiry" target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: "11px 24px", borderRadius: 10, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>Get in Touch</a>
        </div>
      </section>
    </div>
  );
}

function BlogPage(props) {
  var setPageBlogPost = props.setPageBlogPost || function() {};
  var pendingBlogSlug = props.pendingBlogSlug || null;

  // Fetch posts from Sanity
  var sanityPostsResult = useSanity('*[_type == "post"] | order(publishedAt desc) {_id,title,tag,tagColor,excerpt,author,publishedAt,readTime,body[]{...,asset->{_id,url,_ref}},coverImage{...,asset->{_id,url,_ref}},showAsBanner}', []);
  var sanityPosts = sanityPostsResult[0];
  var sanityLoading = sanityPostsResult[1];

  var fallbackPosts = [
    {
      tag: "Data Management", color: "#22A7FF", tagBg: "rgba(34,167,255,0.1)",
      title: "Why Utility Bill Processing Is Costing Your Team Thousands of Hours a Year",
      excerpt: "Manual data entry from utility invoices is one of the most persistent bottlenecks in sustainability reporting. We break down the hidden costs — and the fix.",
      author: "StrategicGreen Team", date: "Feb 4, 2025", readTime: "6 min",
      body: [
        { type: "p", text: "Every sustainability team we speak to has the same story. There is a spreadsheet — sometimes several — that someone in the team updates by hand every month, copying numbers from PDFs into cells. The spreadsheet has grown over time. It has tabs for different sites, different meters, different fuel types. There are formulas nobody fully understands anymore. And it takes days." },
        { type: "h2", text: "The hidden cost of manual extraction" },
        { type: "p", text: "When organisations calculate the cost of their sustainability reporting, they typically count software licences and consultant fees. They rarely count the hours their own team spends processing data. Our research across mid-market and enterprise organisations suggests the average sustainability team spends between 40 and 120 hours per reporting cycle on manual data collection and entry — before any analysis begins." },
        { type: "stat", items: [{ value: "94%", label: "of bills processed without manual input using our platform" }, { value: "10×", label: "faster than manual data teams" }, { value: "100%", label: "data accuracy across all bill formats tested" }] },
        { type: "p", text: "At a blended rate of $80 per hour for a sustainability analyst, that is $3,200 to $9,600 per reporting cycle — just for data entry. For an organisation reporting quarterly, that is $12,800 to $38,400 per year on a task that produces no insight. It simply moves numbers from one format to another." },
        { type: "h2", text: "Why utility bills are uniquely difficult" },
        { type: "p", text: "Utility bills are not designed for machine reading. Each retailer has its own format, its own terminology, and its own logic. A gas bill from one supplier may present consumption in MJ; another uses GJ. An electricity bill might show peak and off-peak demand charges as separate line items, or it might bundle them. Water bills in some jurisdictions include sewerage charges; in others they do not." },
        { type: "callout", text: "The inconsistency is not a bug — it is a feature of a deregulated utility market. But for anyone trying to aggregate data across a portfolio of buildings, it is a significant problem." },
        { type: "p", text: "Traditional extraction tools — OCR software, template-based parsers — attempt to solve this with rules. You define a template for each supplier format, and the tool applies it. This works until the supplier changes their layout, which they do, regularly, without notice. The result is a maintenance burden that grows with every new supplier, every new site, every new country you operate in." },
        { type: "h2", text: "The AI-first approach" },
        { type: "p", text: "StrategicGreen.ai approaches this differently. Instead of templates, we use vision-language models trained on utility documents across formats, languages, and jurisdictions. The model understands the semantic meaning of a bill — it knows that kWh, kilowatt-hours, and unidades de energia all refer to the same thing — and extracts the right data regardless of layout." },
        { type: "p", text: "The result is a system that processes a bill it has never seen before with the same accuracy as one it sees every month. No template. No mapping. No maintenance." },
        { type: "h2", text: "What this means in practice" },
        { type: "list", items: ["Your team stops spending time on data entry and starts spending it on analysis.", "Your data arrives faster — bills can be processed within hours of receipt, not weeks.", "Your accuracy improves — human error in manual entry is eliminated.", "Your coverage expands — you can onboard new suppliers and sites without IT work."] },
        { type: "p", text: "The organisations that move first on utility data automation will have a structural advantage in their sustainability reporting — cleaner data, faster cycles, and lower cost. Those that wait will continue to spend thousands of hours a year on work that a well-designed AI system can do in minutes." }
      ]
    },
    {
      tag: "AI Technology", color: "#D52D9B", tagBg: "rgba(213,45,155,0.1)",
      title: "How AI Reads Any Utility Bill — Without Templates or Mapping",
      excerpt: "Traditional data extraction tools need a template for every billing format. Here is how our AI-first approach eliminates that requirement entirely.",
      author: "StrategicGreen Team", date: "Jan 21, 2025", readTime: "8 min",
      body: [
        { type: "p", text: "The phrase artificial intelligence is used loosely in the sustainability software market. Many tools that describe themselves as AI-powered are, in practice, rule-based parsers with some heuristics bolted on. Understanding the difference matters — because the approach determines whether a tool will still work when a supplier changes their bill layout next quarter." },
        { type: "h2", text: "How traditional extraction works" },
        { type: "p", text: "Template-based extraction is conceptually simple. You take a utility bill, identify the zones where key data appears, and write rules that tell the software where to look. When a bill arrives that matches the template, extraction works well. When it does not match — because the supplier changed their format, because the bill is from a jurisdiction the tool has not seen, because it is printed at a different resolution — extraction fails." },
        { type: "callout", text: "The fundamental problem with template-based approaches is that they confuse memorising with understanding. A template knows where data is on a specific bill. It does not understand what it is reading." },
        { type: "h2", text: "Vision-language models: a different foundation" },
        { type: "p", text: "Vision-language models (VLMs) are trained to understand both the visual layout and the semantic content of documents simultaneously. When a VLM reads a utility bill, it is not looking for data in predefined zones. It is reading the document the way a human expert would — understanding that a table with columns labelled Period, Units, and Rate contains consumption data, regardless of where on the page that table appears." },
        { type: "p", text: "This generalisation capability is what makes the approach robust. A VLM that has been trained on utility documents from Australian electricity retailers, European gas suppliers, and US water utilities has developed an understanding of what utility bills are and what they contain. It can apply that understanding to a bill from a supplier it has never seen before." },
        { type: "h2", text: "What we extract, and how" },
        { type: "list", items: ["Account identifiers — NMI, meter number, account number, site address", "Billing period — start date, end date, number of days", "Consumption — total units, peak/off-peak split where applicable, unit of measure", "Tariff information — rate codes, applicable rates, demand charges", "Cost breakdown — energy charges, network charges, environmental levies, GST", "Utility type — electricity, gas, water, waste, solar export"] },
        { type: "p", text: "Each extraction is validated against a set of cross-checks — does the consumption figure align with the cost at the stated tariff rate? Does the billing period match the dates? Are there anomalies that suggest an estimated read? These checks catch errors before data reaches your systems." },
        { type: "h2", text: "Eight languages, one model" },
        { type: "p", text: "Our model currently processes utility documents in eight languages without requiring separate language configurations. This matters for multinational organisations that procure utilities across different jurisdictions — a single workflow handles bills from Sydney, Singapore, London, and Berlin." },
        { type: "stat", items: [{ value: "8", label: "languages supported natively" }, { value: "2,000+", label: "bill formats tested" }, { value: "0", label: "templates or mappings required" }] },
        { type: "p", text: "The practical implication is that adding a new country or supplier to your portfolio is a configuration change, not a development project. You do not need to involve IT. You do not need to wait for a template to be built. You upload the bill and the model reads it." }
      ]
    },
    {
      tag: "Carbon Accounting", color: "#165895", tagBg: "rgba(22,88,149,0.1)",
      title: "From Raw Bills to Carbon Footprint: The Data Journey Explained",
      excerpt: "Understanding how utility consumption data becomes GHG emissions figures is essential for anyone building a credible carbon accounting programme.",
      author: "StrategicGreen Team", date: "Jan 8, 2025", readTime: "9 min",
      body: [
        { type: "p", text: "Carbon accounting begins with data — specifically, with the activity data that describes how much energy, fuel, or resource your organisation has consumed. For most built-environment organisations, the single largest source of this activity data is utility bills. Understanding how that data travels from a PDF invoice to a verified emissions figure is foundational to building a credible reporting programme." },
        { type: "h2", text: "Step 1: Data extraction" },
        { type: "p", text: "The journey begins with the bill itself. A utility invoice contains the consumption figure for a billing period — typically monthly or quarterly — along with the unit of measure, the meter identifier, the site address, and the billing dates. The first challenge is getting this data out of the document and into a structured format." },
        { type: "p", text: "As discussed elsewhere, this step is where most manual effort and most errors occur. A bill that contains a consumption figure of 45,231 kWh is useful. The same figure misread as 4,523 or 452,310 will propagate through every subsequent calculation and produce a materially wrong emissions figure." },
        { type: "h2", text: "Step 2: Unit normalisation" },
        { type: "p", text: "Utility bills do not use a consistent unit of measure. Gas consumption may be reported in MJ, GJ, therms, cubic metres, or cubic feet depending on the retailer and jurisdiction. Electricity is almost always in kWh, but demand charges may be in kW or kVA. Water may be in kilolitres, megalitres, or cubic metres. Before any emissions calculation can begin, all consumption data must be normalised to a common unit." },
        { type: "callout", text: "Unit errors are the most common source of material misstatement in GHG inventories. A gas bill reported in MJ treated as if it were in GJ will understate emissions by a factor of 1,000." },
        { type: "h2", text: "Step 3: Emissions factor application" },
        { type: "p", text: "Once consumption is in a normalised unit, an emissions factor converts it to a carbon equivalent. Emissions factors vary by fuel type, by grid region, by year, and — for grid electricity — by whether a market-based or location-based methodology is being used." },
        { type: "list", items: ["Scope 1 — direct combustion of fossil fuels on site (gas boilers, diesel generators)", "Scope 2 — purchased electricity, market-based using supplier factors or location-based using grid averages", "Scope 3 — upstream fuel emissions, transmission and distribution losses, water supply and treatment"] },
        { type: "p", text: "The selection of the appropriate emissions factor is not always straightforward. For Australian grid electricity, the National Greenhouse Accounts factors update annually and differ by state. For global portfolios, each jurisdiction has its own factor source with its own update cadence." },
        { type: "h2", text: "Step 4: Aggregation and verification" },
        { type: "stat", items: [{ value: "3", label: "emission scopes calculated automatically" }, { value: "100%", label: "audit trail from bill to tCO₂e" }, { value: "94%", label: "faster than manual calculation workflows" }] },
        { type: "p", text: "With individual meter-level emissions calculated, the data is aggregated to building, portfolio, business unit, or entity level depending on reporting requirements. At each level, cross-checks validate that the sum of parts equals the whole and that year-on-year movements are within expected ranges." },
        { type: "p", text: "The output is a structured emissions dataset with a complete audit trail — from the source document, through extraction, normalisation, factor application, and aggregation. Every figure is traceable to its source. This is what third-party verifiers require, and it is what regulators are beginning to mandate." }
      ]
    },
    {
      tag: "Portfolio Management", color: "#F79964", tagBg: "rgba(247,153,100,0.1)",
      title: "Portfolio Benchmarking for Buildings: What It Is and Why It Matters",
      excerpt: "Benchmarking energy and carbon performance across a building portfolio reveals outliers, sets realistic targets, and drives accountability at asset level.",
      author: "StrategicGreen Team", date: "Dec 17, 2024", readTime: "7 min",
      body: [
        { type: "p", text: "Portfolio benchmarking is the practice of comparing the energy and carbon performance of individual buildings against a reference — whether that reference is a peer group, an industry standard, or the portfolio's own historical performance. For asset managers, property developers, and corporate real estate teams, benchmarking is the foundation of any serious decarbonisation programme." },
        { type: "h2", text: "Why benchmarking matters" },
        { type: "p", text: "Without a benchmark, a consumption figure is almost meaningless. Knowing that a building consumed 2.4 million kWh last year tells you very little on its own. Knowing that this represents an energy use intensity of 180 kWh/m² per year, and that comparable buildings in the same climate zone average 140 kWh/m², tells you that the building is performing 29% worse than its peers — and that there is a real opportunity for improvement." },
        { type: "callout", text: "The buildings that consume the most are rarely the buildings that feel like they consume the most. Benchmarking makes the outliers visible." },
        { type: "h2", text: "The metrics that matter" },
        { type: "list", items: ["Energy Use Intensity (EUI) — total energy consumption normalised by floor area, typically in kWh/m²/year", "Carbon Intensity — total Scope 1 and Scope 2 emissions normalised by floor area, in kgCO₂e/m²/year", "Normalised Performance — EUI adjusted for occupancy, operating hours, and climate", "Year-on-Year Change — absolute and intensity-based change versus prior periods"] },
        { type: "p", text: "The choice of normalisation metric matters. A building that operates 24 hours a day, 7 days a week will naturally consume more than one that operates 8 hours a day, 5 days a week. Comparing them on raw EUI is misleading. Normalising for operating hours gives a fairer picture of underlying efficiency." },
        { type: "h2", text: "From benchmarking to action" },
        { type: "stat", items: [{ value: "Top 20%", label: "outliers typically account for 50%+ of portfolio emissions" }, { value: "3–5 yr", label: "typical payback on data-driven efficiency upgrades" }, { value: "15–30%", label: "typical emission reduction achievable through operational changes alone" }] },
        { type: "p", text: "Benchmarking is not an end in itself — it is a prioritisation tool. Once you know which buildings are underperforming, you can focus investigation and capital expenditure where it will have the most impact. A building that is 40% above peer benchmark on carbon intensity deserves a detailed audit. A building that is 10% below benchmark is probably well-managed and needs only routine monitoring." },
        { type: "p", text: "Portfolio benchmarking also enables accountability. When building managers can see how their asset compares to others in the portfolio, and when that comparison is reported regularly, behaviour changes. The combination of visibility and accountability is more effective than most technical interventions at driving sustained performance improvement." }
      ]
    },
    {
      tag: "Reporting", color: "#22A7FF", tagBg: "rgba(34,167,255,0.1)",
      title: "The 10-Month Reporting Gap: How Real-Time Data Changes Everything",
      excerpt: "Many organisations are still reporting on utility data that is nearly a year old. Here is why that matters — and how automation closes the gap.",
      author: "StrategicGreen Team", date: "Dec 3, 2024", readTime: "5 min",
      body: [
        { type: "p", text: "Most large organisations in Australia report their Scope 1 and Scope 2 emissions once a year — typically in an annual report, a sustainability report, or a regulatory submission. The data in those reports often describes energy consumption from the financial year that ended six to twelve months before the report is published. By the time stakeholders read it, the information is already old." },
        { type: "h2", text: "Where the delay comes from" },
        { type: "p", text: "The delay is not caused by slow reporting teams. It is caused by the data collection process itself. Bills arrive from suppliers on their own schedule — monthly for most, quarterly for some. They are emailed to various inboxes, mailed to registered addresses, or made available through online portals. Someone has to collect them, check them, and enter the data." },
        { type: "list", items: ["Collection lag: bills may be received weeks after the billing period ends", "Processing lag: manual entry queues build up, especially at period-end", "Review lag: data must be reviewed and approved before it can be used", "Reporting lag: the reporting cycle itself adds further delay"] },
        { type: "callout", text: "Across these four sources of delay, many organisations find themselves reporting on data that is 8 to 12 months old by the time it reaches stakeholders." },
        { type: "h2", text: "Why this matters more than it used to" },
        { type: "p", text: "For a long time, annual reporting cadence was accepted as normal because the regulatory and investor expectations it had to satisfy were themselves annual. That is changing. The mandatory climate disclosures being introduced in Australia, the UK, and the EU are beginning to create expectations of more frequent, more timely data. Investors are asking for quarterly updates. Tenants are asking for real-time benchmarking data." },
        { type: "stat", items: [{ value: "10 mo", label: "average delay between bill date and reported emissions" }, { value: "48 hrs", label: "maximum processing time with StrategicGreen.ai" }, { value: "4×", label: "more reporting cycles possible with automated data" }] },
        { type: "h2", text: "What changes with automation" },
        { type: "p", text: "When utility bills are processed automatically — extracted, validated, and loaded into a carbon accounting system within hours of receipt — the entire reporting cadence shifts. Monthly reporting becomes straightforward. Quarterly reporting becomes near real-time. Anomalies are caught within days of occurring, not months." },
        { type: "p", text: "The organisations that invest in automating their utility data infrastructure now will be positioned to meet the reporting expectations of 2026 and 2027 without building new manual processes. Those that do not will face a choice between expensive catch-up projects and continued exposure to regulatory and reputational risk." }
      ]
    }
  ];

  // Tag colour map for Sanity posts
  var tagColorMap = { "#22A7FF": "#22A7FF", "#165895": "#165895", "#D52D9B": "#D52D9B", "#F79964": "#F79964" };
  function resolveColor(c) { return c || "#22A7FF"; }
  function colorBg(c) { var hex = resolveColor(c); return hex + "18"; }

  // Merge: use Sanity posts if any, else fallback
  var posts = (sanityPosts && sanityPosts.length > 0)
    ? sanityPosts.map(function(p) {
        var c = resolveColor(p.tagColor);
        return {
          _id: p._id,
          tag: p.tag || "Article",
          color: c,
          tagBg: colorBg(c),
          title: p.title,
          excerpt: p.excerpt || "",
          author: p.author || "StrategicGreen Team",
          date: p.publishedAt ? p.publishedAt.substring(0, 10) : "",
          readTime: p.readTime || "5 min",
          body: ptToBlocks(p.body),
          coverImage: p.coverImage || null,
          showAsBanner: p.showAsBanner !== false
        };
      })
    : fallbackPosts;

  // Determine which post to show from URL slug
  var activePost = null;
  if (pendingBlogSlug && posts.length > 0) {
    for (var pi = 0; pi < posts.length; pi++) {
      if (slugify(posts[pi].title) === pendingBlogSlug) {
        activePost = posts[pi];
        break;
      }
    }
  }

  function openPost(post) {
    setPageBlogPost(post);
  }

  function closePost() {
    window.history.pushState({ page: "Blog", blogSlug: null }, "", "/blog");
    setPageBlogPost(null);
  }

  if (activePost) {
    return <BlogDetailPage post={activePost} onBack={closePost} />;
  }

  if (sanityLoading && (!sanityPosts || sanityPosts.length === 0)) {
    return (
      <div style={{ paddingTop: 70 }}>
        <section style={{ padding: "80px 24px 56px", textAlign: "center" }}>
          <div className="section-label fade-up" style={{ marginBottom: 16 }}>Blog</div>
          <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,54px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.06, marginBottom: 16, color: "#04100D" }}>
            Utility data intelligence,<br /><span className="gradient-text">clearly explained.</span>
          </h1>
          <p style={{ fontSize: 17, color: "#64748b" }}>Loading articles...</p>
        </section>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 70 }}>
      <section style={{ padding: "80px 24px 56px", textAlign: "center" }}>
        <div className="section-label fade-up" style={{ marginBottom: 16 }}>Blog</div>
        <h1 className="fade-up-1" style={{ fontSize: "clamp(30px,5vw,54px)", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.06, marginBottom: 16, color: "#04100D" }}>
          Utility data intelligence,<br /><span className="gradient-text">clearly explained.</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: 17, color: "#64748b", lineHeight: 1.7, maxWidth: 500, margin: "0 auto" }}>
          Insights on utility data management, AI extraction, carbon accounting, and portfolio sustainability.
        </p>
      </section>
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 100px" }}>
        <div className="blog-card blog-featured" onClick={function() { openPost(posts[0]); }} style={{ marginBottom: 32, display: "grid", gridTemplateColumns: "1fr 1fr", background: "#04100D", minHeight: 300, cursor: "pointer" }}>
          <div style={{ padding: "48px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <span className="tag" style={{ marginBottom: 16, display: "inline-block", background: "rgba(34,167,255,0.18)", color: "#22A7FF" }}>{posts[0].tag}</span>
              <h2 style={{ fontSize: "clamp(18px,2.5vw,28px)", fontWeight: 800, color: "#fff", lineHeight: 1.35, marginBottom: 16, letterSpacing: "-0.5px" }}>{posts[0].title}</h2>
              <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.75 }}>{posts[0].excerpt}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#22A7FF,#D52D9B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700 }}>SG</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>{posts[0].author}</div>
                  <div style={{ fontSize: 12, color: "#475569" }}>{posts[0].date} · {posts[0].readTime} read</div>
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#22A7FF" }}>Read article →</span>
            </div>
          </div>
          {(function() {
            var imgUrl = posts[0].coverImage ? sanityImg(posts[0].coverImage, 800) : null;
            if (imgUrl) {
              return <div style={{ overflow: "hidden", borderRadius: "0 12px 12px 0" }}>
                <img src={imgUrl} alt={posts[0].title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>;
            }
            return <div style={{ background: "linear-gradient(135deg,rgba(34,167,255,0.12),rgba(213,45,155,0.12))", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <Icon name="file-text" size={36} color="rgba(255,255,255,0.45)" strokeWidth={1.4}/>
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>Featured post</div>
              </div>
            </div>;
          })()}
        </div>
        <div className="blog-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 380px))", gap: 24, justifyContent: "start" }}>
          {posts.slice(1).map(function(post, i) {
            return (
              <div key={i} className="blog-card" onClick={(function(p) { return function() { openPost(p); }; })(posts[i + 1])} style={{ background: "#fff", cursor: "pointer" }}>
                {(function() {
                  var imgUrl = post.coverImage ? sanityImg(post.coverImage, 600) : null;
                  if (imgUrl) {
                    return <div style={{ height: 180, overflow: "hidden", borderRadius: "12px 12px 0 0" }}>
                      <img src={imgUrl} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </div>;
                  }
                  return <div style={{ height: 140, background: "linear-gradient(135deg," + post.color + "18," + post.color + "28)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 52, height: 52, borderRadius: 13, background: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name="file-text" size={26} color={post.color} strokeWidth={1.6}/>
                    </div>
                  </div>;
                })()}
                <div style={{ padding: "22px" }}>
                  <span className="tag" style={{ marginBottom: 10, display: "inline-block" }}>{post.tag}</span>
                  <h3 style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.45, marginBottom: 10, color: "#04100D" }}>{post.title}</h3>
                  <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7, marginBottom: 18 }}>{post.excerpt.substring(0, 100)}…</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{post.readTime} read</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: post.color }}>Read →</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function LegalPage(props) {
  var title = props.title;
  var updated = props.updated;
  var sections = props.sections;
  var setPage = props.setPage;
  useEffect(function() { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);
  return (
    <div style={{ paddingTop: 70 }}>
      <section style={{ padding: "64px 24px 48px", background: "linear-gradient(180deg,rgba(34,167,255,0.04) 0%,transparent 100%)", position: "relative", overflow: "hidden" }}>
        <div className="dot-grid" style={{ position: "absolute", inset: 0, opacity: 0.3 }}/>
        <div style={{ maxWidth: 760, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div className="section-label" style={{ marginBottom: 16 }}>Legal</div>
          <h1 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 900, letterSpacing: "-1.5px", lineHeight: 1.06, color: "#04100D", marginBottom: 16 }}>{title}</h1>
          <p style={{ fontSize: 14, color: "#94a3b8" }}>Last updated: {updated}</p>
        </div>
      </section>
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 100px" }}>
        {sections.map(function(s, i) {
          return (
            <div key={i} style={{ marginBottom: 40 }}>
              {s.heading && <h2 style={{ fontSize: "clamp(17px,2.5vw,22px)", fontWeight: 800, color: "#04100D", letterSpacing: "-0.4px", marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid #f1f5f9" }}>{s.heading}</h2>}
              {s.paragraphs && s.paragraphs.map(function(p, j) { return <p key={j} style={{ fontSize: 15, color: "#475569", lineHeight: 1.85, marginBottom: 14 }}>{p}</p>; })}
              {s.items && <ul style={{ margin: "12px 0 0", padding: 0, listStyle: "none" }}>{s.items.map(function(item, j) { return <li key={j} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22A7FF", marginTop: 9, flexShrink: 0 }}/><span style={{ fontSize: 15, color: "#475569", lineHeight: 1.75 }}>{item}</span></li>; })}</ul>}
            </div>
          );
        })}
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 32, marginTop: 16, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <button onClick={function() { setPage("Home"); }} className="btn-outline" style={{ padding: "11px 24px", borderRadius: 10, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Home
          </button>
          <a href="https://mail.google.com/mail/?view=cm&to=communications@strategicgreenai.com" target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: "11px 24px", borderRadius: 10, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>Contact Us</a>
        </div>
      </section>
    </div>
  );
}

function PrivacyPage(props) {
  var FALLBACK_PRIVACY = [
    { heading: null, paragraphs: ["This Privacy Policy describes how Strategic Green Innovation Pty Ltd describes how StrategicGreen.ai collects, uses, and protects information when you use our platform, website, and services. By using our services, you agree to the practices described in this policy."] },
    { heading: "1. Information We Collect", paragraphs: ["We collect information you provide directly to us and information generated through your use of our services."], items: ["Account information — name, email address, organisation name, and job title when you register.", "Usage data — pages visited, features used, session duration, and interaction patterns within the platform.", "Uploaded documents — utility bills and invoices you upload for processing. These are used solely to deliver the extraction service.", "Communications — emails and messages you send to our support or sales team.", "Payment information — billing details processed securely through our payment provider. We do not store full card numbers."] },
    { heading: "2. How We Use Your Information", paragraphs: ["We use the information we collect to operate, improve, and secure our services."], items: ["To provide and deliver the data extraction, carbon accounting, and benchmarking services you have requested.", "To process payments and manage your subscription.", "To send transactional emails such as receipts, onboarding guides, and platform notifications.", "To respond to support requests and troubleshoot technical issues.", "To analyse usage patterns and improve platform performance and features.", "To comply with legal obligations and enforce our Terms of Service."] },
    { heading: "3. Data Storage and Security", paragraphs: ["Your data is stored on secure cloud infrastructure hosted on Amazon Web Services (AWS). We implement industry-standard security measures including encryption in transit (TLS) and at rest, access controls, and regular security reviews.", "Uploaded utility documents are retained only as long as necessary to deliver the service and are subject to your organisation's data retention settings. You may request deletion of your data at any time by contacting us."] },
    { heading: "4. Data Sharing", paragraphs: ["We do not sell your personal information. We share data only in the following limited circumstances:"], items: ["Service providers — trusted third parties who help us operate the platform (e.g. cloud hosting, payment processing, analytics). All providers are bound by data processing agreements.", "Legal compliance — where required by law, regulation, or valid legal process.", "Business transfers — in connection with a merger, acquisition, or sale of assets, with appropriate confidentiality obligations.", "With your consent — for any other purpose with your explicit permission."] },
    { heading: "5. Cookies and Tracking", paragraphs: ["We use cookies and similar technologies to maintain session state, remember your preferences, and analyse platform usage. You can control cookie settings through your browser.", "We use analytics tools to understand how users interact with our platform. This data is aggregated and does not identify you individually."] },
    { heading: "6. Your Rights", paragraphs: ["Depending on your location, you may have rights including:"], items: ["Access — request a copy of the personal data we hold about you.", "Correction — request correction of inaccurate or incomplete data.", "Deletion — request deletion of your personal data, subject to legal retention requirements.", "Portability — receive your data in a structured, machine-readable format.", "Objection — object to certain types of processing, including direct marketing."] },
    { heading: "7. International Data Transfers", paragraphs: ["StrategicGreen.ai operates globally. Your data may be processed in countries other than your own. We ensure appropriate safeguards are in place for any international transfers, including standard contractual clauses where required."] },
    { heading: "8. Contact Us", paragraphs: ["If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at communications@strategicgreenai.com. We aim to respond within 30 days."] }
  ];
  var result = useSanity('*[_type == "siteSettings"][0]{privacyPolicy,privacyUpdated}', null);
  var data = result[0];
  var raw = data && data.privacyPolicy && data.privacyPolicy.length > 0 ? data.privacyPolicy : null;
  var sections = raw ? raw.map(function(s) {
    return { heading: s.heading || null, paragraphs: s.body ? [s.body] : [], items: s.items || [] };
  }) : FALLBACK_PRIVACY;
  var updated = (data && data.privacyUpdated) ? data.privacyUpdated : "1 January 2026";
  return <LegalPage title="Privacy Policy" updated={updated} sections={sections} setPage={props.setPage} />;
}

function TermsPage(props) {
  var FALLBACK_TERMS = [
    { heading: null, paragraphs: ["These Terms of Service govern your access to and use of the StrategicGreen.ai platform, website, and services provided by Strategic Green Innovation Pty Ltd. Please read these Terms carefully before using our services."] },
    { heading: "1. Acceptance of Terms", paragraphs: ["By creating an account, accessing the platform, or using any of our services, you agree to be bound by these Terms and our Privacy Policy. If you are using the services on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms."] },
    { heading: "2. Description of Services", paragraphs: ["StrategicGreen.ai provides AI-powered utility bill data extraction, GHG carbon accounting, portfolio benchmarking, and related sustainability data management services. The specific features available to you depend on the subscription plan you have selected."] },
    { heading: "3. Account Responsibilities", paragraphs: ["You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account."], items: ["You must provide accurate and complete information when creating your account.", "You must not share your login credentials with unauthorised parties.", "You must notify us immediately of any suspected unauthorised access.", "You must not use the platform for any unlawful purpose or in violation of these Terms.", "You are responsible for ensuring your use of the platform complies with applicable laws in your jurisdiction."] },
    { heading: "4. Subscription and Payment", paragraphs: ["Paid subscriptions are billed in advance on a monthly or annual basis as selected at the time of purchase. All fees are non-refundable except as required by applicable law.", "We reserve the right to change subscription pricing with reasonable notice. Continued use of the services after a price change constitutes acceptance of the new pricing.", "Failure to pay may result in suspension or termination of your access to the platform."] },
    { heading: "5. Acceptable Use", paragraphs: ["You agree not to misuse our services. Prohibited activities include:"], items: ["Attempting to gain unauthorised access to our systems or other users accounts.", "Uploading malicious files, viruses, or harmful code.", "Using automated scripts or bots to access the platform without prior written consent.", "Reverse engineering, decompiling, or attempting to extract the source code of our platform.", "Reselling or sublicensing access to the platform without our written authorisation.", "Using the platform to process data in violation of applicable data protection laws."] },
    { heading: "6. Intellectual Property", paragraphs: ["All intellectual property rights in the StrategicGreen.ai platform, including software, AI models, user interface, and documentation, remain with Strategic Green Innovation Pty Ltd.", "You retain ownership of the data you upload to our platform. By uploading data, you grant us a limited licence to process that data solely for the purpose of delivering the services to you.", "Our trademarks, logos, and brand identifiers may not be used without our prior written consent."] },
    { heading: "7. Data and Privacy", paragraphs: ["Our collection and use of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. You acknowledge that you have read and understood our Privacy Policy."] },
    { heading: "8. Disclaimer of Warranties", paragraphs: ["Our services are provided as is and as available without warranties of any kind, either express or implied. We do not warrant that the services will be uninterrupted, error-free, or completely accurate.", "Carbon accounting outputs and data extractions are provided for informational purposes. You are responsible for verifying the accuracy of outputs before using them for regulatory submissions or financial disclosures."] },
    { heading: "9. Limitation of Liability", paragraphs: ["To the maximum extent permitted by law, Strategic Green Innovation Pty Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the services. Our total liability to you shall not exceed the fees you paid to us in the three months preceding the claim."] },
    { heading: "10. Termination", paragraphs: ["You may terminate your account at any time by contacting us. We may suspend or terminate your access if you breach these Terms, fail to pay fees, or for other legitimate business reasons with reasonable notice.", "Upon termination, your right to use the services ceases immediately. We will retain your data in accordance with our Privacy Policy and applicable legal requirements."] },
    { heading: "11. Governing Law", paragraphs: ["These Terms are governed by the laws of New South Wales, Australia. Any disputes shall be subject to the exclusive jurisdiction of the courts of New South Wales, unless otherwise required by applicable consumer protection law in your jurisdiction."] },
    { heading: "12. Contact", paragraphs: ["For questions about these Terms, please contact us at communications@strategicgreenai.com."] }
  ];
  var result = useSanity('*[_type == "siteSettings"][0]{termsOfService,termsUpdated}', null);
  var data = result[0];
  var raw = data && data.termsOfService && data.termsOfService.length > 0 ? data.termsOfService : null;
  var sections = raw ? raw.map(function(s) {
    return { heading: s.heading || null, paragraphs: s.body ? [s.body] : [], items: s.items || [] };
  }) : FALLBACK_TERMS;
  var updated = (data && data.termsUpdated) ? data.termsUpdated : "1 January 2026";
  return <LegalPage title="Terms of Service" updated={updated} sections={sections} setPage={props.setPage} />;
}

export default function App() {
  var initRoute = getInitialRoute();

  var pageState = useState(initRoute.page);
  var page = pageState[0];
  var setPageRaw = pageState[1];

  var blogSlugState = useState(initRoute.blogSlug);
  var pendingBlogSlug = blogSlugState[0];
  var setPendingBlogSlug = blogSlugState[1];

  var scrolledState = useState(false);
  var scrolled = scrolledState[0];
  var setScrolled = scrolledState[1];

  var mobileState = useState(false);
  var mobileOpen = mobileState[0];
  var setMobileOpen = mobileState[1];

  var earlyAccessState = useState(false);
  var earlyAccessOpen = earlyAccessState[0];
  var setEarlyAccessOpen = earlyAccessState[1];

  // setPage: update URL + state
  function setPage(pg) {
    navigate(pg, null);
    setPageRaw(pg);
    setPendingBlogSlug(null);
  }

  // Navigate directly to a blog post
  function setPageBlogPost(post) {
    if (!post) {
      window.history.pushState({}, "", "/blog");
      setPageRaw("Blog");
      setPendingBlogSlug(null);
      applyPageSEO("Blog");
      return;
    }
    navigate("Blog", post);
    setPageRaw("Blog");
    setPendingBlogSlug(slugify(post.title));
    applyPageSEO("Blog", post);
  }

  // Handle browser back/forward
  useEffect(function() {
    function onPop() {
      var r = getInitialRoute();
      setPageRaw(r.page);
      setPendingBlogSlug(r.blogSlug);
    }
    window.addEventListener("popstate", onPop);
    return function() { window.removeEventListener("popstate", onPop); };
  }, []);

  useEffect(function() {
    var handler = function() { setScrolled(window.scrollY > 20); };
    window.addEventListener("scroll", handler);
    return function() { window.removeEventListener("scroll", handler); };
  }, []);

  useEffect(function() {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileOpen(false);
    applyPageSEO(page);
  }, [page]);

  // ── Custom cursor tracker (dot + ring) ──
  useEffect(function() {
    var ring = document.getElementById("cur-ring");
    if (!ring) return;

    var mx = -100; var my = -100;
    var rx = -100; var ry = -100;
    var raf = null;

    function onMove(e) {
      mx = e.clientX; my = e.clientY;
      var el = document.elementFromPoint(mx, my);
      var isHot = el && (el.closest("button, a, .mag-btn, .tilt-card, .cap-tag, .nav-link") !== null);
      if (isHot) { ring.classList.add("hovered"); } else { ring.classList.remove("hovered"); }
    }
    function tick() {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      ring.style.left = rx + "px";
      ring.style.top  = ry + "px";
      raf = requestAnimationFrame(tick);
    }
    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return function() {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  function renderPage() {
    if (page === "Home")      return <HomePage setPage={setPage} setEarlyAccessOpen={setEarlyAccessOpen} />;
    if (page === "Solutions") return <SolutionsPage setPage={setPage} setEarlyAccessOpen={setEarlyAccessOpen} />;
    if (page === "Pricing")   return <PricingPage setEarlyAccessOpen={setEarlyAccessOpen} />;
    if (page === "About")     return <AboutPage />;
    if (page === "FAQ")       return <FAQPage />;
    if (page === "Blog")      return <BlogPage setPageBlogPost={setPageBlogPost} pendingBlogSlug={pendingBlogSlug} />;
    if (page === "Privacy")   return <PrivacyPage setPage={setPage} />;
    if (page === "Terms")     return <TermsPage setPage={setPage} />;
    return <HomePage setPage={setPage} setEarlyAccessOpen={setEarlyAccessOpen} />;
  }

  // Expose setPage globally for cookie banner Privacy Policy link
  window.__sga_setPage = setPage;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: globalCSS }} />
      <div id="cur-ring" className="cursor-ring" />
      <Nav page={page} setPage={setPage} scrolled={scrolled} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} setEarlyAccessOpen={setEarlyAccessOpen} />
      <main>{renderPage()}</main>
      <Footer setPage={setPage} />
      {earlyAccessOpen && <EarlyAccessModal onClose={function() { setEarlyAccessOpen(false); }} />}
      <CookieBanner />
    </>
  );
}
